import {
	calculateBreakTime,
	calculateDurationSince,
	formatDate,
	formatDurationSince,
	formatTime,
	getCurrentISOOffset,
	getISOZonedDateTime,
	isSameDay,
	normalizeTimeInput,
	parseDateInput,
	parseDateTimeZone,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {api, RequestParam} from "@/shared/api";
import {Combobox} from "@/shared/ui/combobox";
import {DescriptionTypeahead} from "@/shared/ui/descriptionTypeahead";
import {FormUtil} from "@/shared/forms/formUtil";
import {ProjectTimeInfoPanel} from "@/shared/projecttime/projectTimeInfoPanel";
import {SwitchResumeCombinedRecordingDialog} from "./switchResumeCombinedRecordingDialog";
import {PauseCombinedRecordingDialog} from "./pauseCombinedRecordingDialog";
import {TaskDropdownWithRecommendations} from "@/shared/task/taskDropdownWithRecommendations";
import {WorkingTimeInfoPanel} from "@/shared/workingtime/workingTimeInfoPanel";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {
	isProjectTimeDateTimeChangeAllowed,
	isRunningWorkingTimeAttachmentEligible,
	isRunningWorkingTimeTypeChangeAllowed,
	isWorkingTimeDateTimeChangeAllowed
} from "@/shared/securityHelper";
import {breadcrumbsWithoutName, stringNotBlank} from "@/shared/utils/string";
import {
	getModeForType,
	isProjectTimeIncluded,
	isWorkingTimeIncluded,
	RecordingType
} from "@/features/timeline/recordingProcedure";
import * as Input from "@/shared/utils/input";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {formatCustomField} from "@/shared/utils/misc";
import {Notify} from "@/shared/ui/notify";
import {DOMUtil} from "@/shared/utils/dom";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	CombinedRecordingStatus,
	CustomFieldDefinition,
	CustomFieldOption,
	ErrorResponse,
	ProjectTime,
	Record,
	RunningEntry,
	RunningProjectTime,
	RunningWorkingTime,
	Task,
	WorkingTime,
	WorkingTimeType
} from "@/shared/timr";
import {
	BreakTimeRecordingMode,
	CustomFieldsForRecordType,
	RecordingMode,
	RecordingProcedure,
	RunningTimeStatus,
	WorkingTimeTypeCategory
} from "@/shared/timrTypes";

export type CombinedRecordingButtonAction = "delete" | "switch" | "stop";

export type DescriptionToggleMode = "clear" | "add" | "required";

type ResumeAction = "workingTime" | "both" | "switchDialog";

export interface CombinedRecordingStatusPanelConfig {
	panel: JQuery;
	customFieldDefinitionsWorkingTime: CustomFieldDefinition[];
	customFieldDefinitionsProjectTime: CustomFieldDefinition[];
	workingTimeTypes: WorkingTimeType[];
	workingTimeTypesForRecording: WorkingTimeType[];
	breakTimeTypes: WorkingTimeType[];
	recordingMode: RecordingMode;
	activeRecordingProcedure: RecordingProcedure; // configured recording scope of this panel (WT/PT/BOTH), not the currently running state and not a user-triggered action.
	onUpdateTimeline: (status: CombinedRecordingStatus, forceReload?: boolean) => void;
	onStatusUpdate: (status: CombinedRecordingStatus) => void;
}

/**
 * Panel to view and manage working time and/or project time together
 * Has fields working time and/or project time like working time type, task selection as well as description and custom fields
 * Contains actions to execute endpoints for start, stop, switch, delete
 * The panel is automatically synchronized
 * For the moment it's not possible to manually pause recordings, but the paused state is visualized, see TIMR-9579
 *
 * DEVNOTE: There are certainly areas that can be improved, but a requirement was a fast version of this combined recording panel
 * 			Therefore it is the code is basically a compilation of copies from the working time/project time and recording status panel
 */
export class CombinedRecordingStatusPanel {
	// buttons
	private static BUTTON_CLASS_DEFAULT = "btn-default";
	private static BUTTON_CLASS_PRIMARY = "btn-primary";
	private static BUTTON_CLASS_XLARGE = "btn-xlg";

	// constants
	private static UPDATE_TIMEOUT_PAUSE_MS = 100; // 0.1 seconds
	private static UPDATE_TIMEOUT_MS = 1500; // 1.5 seconds
	private static PERIODICAL_UPDATER_INTERVAL_MS = 20000; // 20 seconds
	private static PREVENT_SESSION_TIMEOUT_INTERVAL_MS = 900000; // 15 minutes

	// status
	private currentStatus: CombinedRecordingStatus;

	// sync
	private changedFields: Set<string> = new Set<string>();
	private updateTimeout: number = null;
	private waitForUpdateTimeout: number;
	private updateTransaction: JQuery.jqXHR<CombinedRecordingStatus> = null;
	private periodicalExecutor: number;
	private currentFocusedField: string;

	// flags for changed start or end date time fields
	// these are used to correctly apply the changed state for each action (switch, stop, etc.) for different modes to each record
	private changedWorkingTimeStart: boolean = false;
	private changedProjectTimeStart: boolean = false;
	private changedEnd: boolean = false;

	// field components
	private workingTimeInfoPanel: WorkingTimeInfoPanel;
	private workingTimeDescriptionTypeahead: DescriptionTypeahead;
	private projectTimeInfoPanel: ProjectTimeInfoPanel;
	private taskDropdown: TaskDropdownWithRecommendations;
	private projectTimeDescriptionTypeahead: DescriptionTypeahead;
	private switchResumeCombinedRecordingDialog: SwitchResumeCombinedRecordingDialog;
	private workingTimeCustomFieldComboboxes: Combobox<CustomFieldOption>[] = [];
	private projectTimeCustomFieldComboboxes: Combobox<CustomFieldOption>[] = [];

	constructor(private config: CombinedRecordingStatusPanelConfig) {
		// Firefox restores JS-set hidden inputs on a normal reload (Chrome/Safari don't); a restored
		// recordId/version makes the first status fetch look unchanged, so the running record's fields
		// (note, start/end, ...) are never repopulated and a stale/empty value is submitted on stop. See TIMR-12482.
		this.config.panel.find(".recordId").val(null);
		this.config.panel.find(".version").val(null);

		this.init();
	}

	public get combinedRecordingStatus(): CombinedRecordingStatus {
		return this.currentStatus;
	}

	/**
	 * Handles the quick switch process for combined recording, ensuring the provided procedure and timings are valid and applicable.
	 *
	 * @param {RecordingProcedure} quickSwitchProcedure - The target procedure for the switch action e.g. if select item is working time -> WORKING_TIME, project time, or both
	 * 													  according to google docs sheet in TIMR-9883
	 * @param {WorkingTime} workingTime - The allocated working time details associated with the recording process.
	 * @param {ProjectTime} projectTime - The designated project time applicable to the recording task.
	 * @return {void} This method does not return a value.
	 */
	public quickSwitchCombinedRecording(quickSwitchProcedure: RecordingProcedure, workingTime: WorkingTime, projectTime: ProjectTime): void {
		this.quickSwitchRecordingIfValid(quickSwitchProcedure, workingTime, projectTime);
	}

	/**
	 * <p>Switch or start a project time based on the given task.</p>
	 * This is used in quick switch or start via the task info dialog. If a project time recording is already running, it switches the recording to the specified task ID.
	 * If no project time running, it starts a new recording associated with the specified task ID.
	 *
	 * @param task - The task to switch or start the recording process for.
	 */
	public switchOrStartWithTask(task: Task): void {
		if (this.isProjectTimeRunning()) {
			// switch project time recording to the selected task ID
			this.shouldSwitchRecording(RecordingProcedure.ProjectTime, task.id);
		} else {
			// select task and start recording
			this.selectTask(task);
			this.shouldStartRecording(RecordingProcedure.ProjectTime);
		}
	}

	/**
	 * Main method for updating the recording panel.
	 * Uses <code>recordingStatus</code> received from backend to update the data of buttons, fields, selections and flow status considering the <code>procedure</code>.
	 *
	 * @param recordingStatus status object received from endpoint
	 * @param procedure 	Based on the specified procedure, the fields of working time, project time or both are updated
	 * 				e.g. only working time is running, a project time note is entered.
	 * 				If the status is now queried, WORKING_TIME is provided as procedure and project time fields aren't updated (which would override the entered note)
	 */
	public updateStatus(recordingStatus: CombinedRecordingStatus, procedure: RecordingProcedure): void {
		// updates changed field flag based on the current recording status and procedure
		this.updateChangedFieldsFlag(recordingStatus, procedure);

		// update current status variable, it's important (!) that this is done before the following calling update functions
		this.currentStatus = recordingStatus;

		this.onStatusUpdate(procedure);
		this.updateButtons();
		this.updateDurationVisibility();
		this.updateFields(procedure);
		this.updateFlowState();
	}

	/**
	 * Initialize event handler, periodical updater, handling for working time/project time and custom fields
	 */
	private init(): void {
		// init events for action buttons 'start', 'stop', 'delete' and 'switch' (in case of combined mode there is also context menu with hover state)
		this.initActionButtons();


		this.initDateFieldListeners();

		// verify and prevent unsaved changes by showing a message
		window.addEventListener("beforeunload", e => {
			if (this.updateTimeout != null || this.updateTransaction != null) {
				e.preventDefault();
				return messages["recording.unsavedChanges.alert"];
			}
		});

		// skip hidden fields for tabbing
		this.config.panel.find("hidden").prop("tabindex", "-1");

		this.switchResumeCombinedRecordingDialog = new SwitchResumeCombinedRecordingDialog({
			container: $("#switchCombinedRecordingDialog"),
			workingTimeTypes: this.config.workingTimeTypesForRecording,
			recordingMode: this.config.recordingMode
		});
		$(this.switchResumeCombinedRecordingDialog).on(SwitchResumeCombinedRecordingDialog.EVENT_CLOSED, () => this.activateUpdater());

		if (this.isWorkingTimeEnabled()) {
			this.initCustomFields("workingTime");
			this.initDescriptionField("workingTime");
			this.initWorkingTimeFields();
		}

		if (this.isProjectTimeEnabled()) {
			this.initCustomFields("projectTime");
			this.initDescriptionField("projectTime");
			this.initProjectTimeFields();
		}

		// update initially and start logic the update circle
		this.fetchRecordingStatus();
		this.startPeriodicalUpdate();
		this.startTimeUpdater();

		// start project time toggle is not available in strict mode
		if (this.config.recordingMode !== RecordingMode.STRICT) {
			this.config.panel.find("#includeProjectTimeFormGroup .checkbox-switchery").click(() => {
				const isProjectTimeIncluded = this.isProjectTimeIncluded();
				this.config.panel.find("[data-project-time-field]").toggle(isProjectTimeIncluded);
			})
		}

		// activate/deactivate updater based on focus on window
		$(window)
			.focus(() => this.activateUpdater())
			.blur(() => this.deactivateUpdater());

		// prevent session timeout after 15 minutes
		setInterval(() => this.fetchRecordingStatus(), CombinedRecordingStatusPanel.PREVENT_SESSION_TIMEOUT_INTERVAL_MS);
	}

	/**
	 * Initializes the description field for the specified recording type.
	 * This is about listening to input changes to queue updates and auto-expanding the textarea on input/change events.
	 * @param type The recording type for which to initialize the description field (either "workingTime" or "projectTime").
	 * @private
	 */
	private initDescriptionField(type: RecordingType) {
		this.config.panel.find(`textarea[name="${type}.description"]`)
			.on("keyup", () => {
				this.queueUpdate(type);
			})
			.each((i, e) => {
				$(e).on("input change", e => FormUtil.autoExpand(e.target));
			});
	}

	/**
	 * Reloads the page if the backend returned no recording status, which happens when combined recording became unavailable for the user since the page was rendered.
	 * After the reload the page is rendered without the combined recording panel.
	 *
	 * @param status status object received from endpoint
	 * @return true if the page is being reloaded and the response must not be processed further
	 */
	private reloadIfRecordingUnavailable(status: CombinedRecordingStatus | null): status is null {
		if (status) {
			return false;
		}
		window.location.reload();
		return true;
	}

	/**
	 * Updates the changed field flag based on the current recording status and procedure.
	 * This method checks the previous running working time and project time against the current status.
	 * If there is a change in the status, it clears the changed flags.
	 *
	 * @param recordingStatus The current combined recording status.
	 * @param procedure The recording procedure to check.
	 */
	private updateChangedFieldsFlag(recordingStatus: CombinedRecordingStatus, procedure: RecordingProcedure): void {
		const previousRunningWorkingTime = this.getRunningWorkingTime();
		const previousRunningProjectTime = this.getRunningProjectTime();
		const currentRunningWorkingTime = recordingStatus.workingTimeStatus?.runningWorkingTime;
		const currentRunningProjectTime = recordingStatus.projectTimeStatus?.runningProjectTime;

		const hasRunningRecordChanged = (previousRunningEntry: RunningEntry, currentRunningEntry: RunningEntry): boolean =>
			!!previousRunningEntry && (!currentRunningEntry || previousRunningEntry.recordId !== currentRunningEntry.recordId);

		if (!previousRunningWorkingTime && currentRunningWorkingTime ||
			!previousRunningProjectTime && currentRunningProjectTime) {
			// This is also called when the page is loaded. In this case we must not fire the recording changed event because otherwise the table would be loaded twice
			this.clearChangedFields(procedure);
		} else if (hasRunningRecordChanged(previousRunningWorkingTime, currentRunningWorkingTime) ||
			hasRunningRecordChanged(previousRunningProjectTime, currentRunningProjectTime)) {
			this.clearChangedFields(procedure);
		} else if (previousRunningWorkingTime?.status !== currentRunningWorkingTime?.status ||
			previousRunningProjectTime?.status !== currentRunningProjectTime?.status) {
			// If pause/resume is clicked, changed flags must also be reset. Otherwise, break time isn't increased if it was manually changed before
			this.clearChangedFields(procedure);
		}
	}

	/**
	 * Updates the status elements and synchronizes the changed variables based on the current procedure.
	 * This method updates the running working time and project time elements,
	 * and sets the changed flags for working time and project time based on the procedure.
	 *
	 * @param procedure The current recording procedure.
	 */
	private onStatusUpdate(procedure: RecordingProcedure): void {
		statusElementsHolder.updateRunningWorkingTime(this.getRunningWorkingTime());
		statusElementsHolder.updateRunningProjectTime(this.getRunningProjectTime());

		// synchronize the changed variables based on the status received from the backend (running records)
		// these are used to correctly apply the changed state for each action (switch, stop, etc.) for different modes to each record
		if (isWorkingTimeIncluded(procedure)) {
			this.changedWorkingTimeStart = this.getRunningWorkingTime()?.changed ?? false;
		}
		if (isProjectTimeIncluded(procedure)) {
			this.changedProjectTimeStart = this.getRunningProjectTime()?.changed ?? false;
		}

		this.config.onStatusUpdate(this.currentStatus);
	}

	/**
	 * Return value of switchery to start working without project time
	 */
	private isProjectTimeIncluded(): boolean {
		return this.config.panel.find("#includeProjectTimeSwitchery").is(":checked");
	}

	/**
	 * Checks if the selected working time type is attendance time.
	 *
	 * @returns {boolean} True if the selected working time type is attendance time, false otherwise.
	 */
	private isAttendanceTimeSelected(): boolean {
		return this.getSelectedWorkingTimeType()?.category === WorkingTimeTypeCategory.AttendanceTime;
	}

	/**
	 * Initialize working time type select, fields, info component, description field
	 */
	private initWorkingTimeFields(): void {
		this.initToggleDescriptionButton("workingTime");

		const workingTimeTypeSelect = this.config.panel.find("[name='workingTime.workingTimeType.id']");
		workingTimeTypeSelect
			.empty()
			.change(e => this.selectWorkingTimeType($(e.target).find("option:selected").data("workingTimeType") as WorkingTimeType));
		DOMUtil.fillWorkingTimeTypeSelect(workingTimeTypeSelect, this.config.workingTimeTypesForRecording, false);

		this.workingTimeDescriptionTypeahead = new DescriptionTypeahead({
			element: this.config.panel.find("[name='workingTime.description']").closest(".input-group"),
			url: "filterWorkingTimeDescriptions.json"
		});

		// listen to changes of the description typeahead suggestion selection
		$(this.workingTimeDescriptionTypeahead).on(DescriptionTypeahead.EVENT_CHANGED, () => {
			//get the updated description value and trigger a change event (for a correct height of the field)
			const description = this.config.panel.find("[name='workingTime.description']").change();

			// update the description field and toggle button
			this.updateDescriptionField("workingTime", false, description.val() as string);

			this.queueUpdate("workingTime");
		});

		this.workingTimeInfoPanel = new WorkingTimeInfoPanel(this.config.panel.find("#workingTimeInfoPanel"));
		this.onWorkingTimeTypeChanged();
	}

	/**
	 * Initialize project time task selection, info panel inclusive budget component, description field, switch dialog
	 */
	private initProjectTimeFields(): void {
		this.initToggleDescriptionButton("projectTime");

		this.taskDropdown = new TaskDropdownWithRecommendations({
			rootElement: this.config.panel.find("[name='projectTime.task.id']").closest(".input-group"),
			allowSelectionOfNotBookable: false,
			hideInactive: true,
			emptyMessage: messages["task.select.task"],
			clearable: false,
			getReferenceDate: () => this.getStart("projectTime") ?? Temporal.Now.zonedDateTimeISO()
		}, {
			loadRootNodesURL: "recording/loadAllTasks.json",
			loadSubNodesURL: "recording/treeLoad.json",
			filterNodesURL: "recording/filterTasks.json"
		});
		$(this.taskDropdown).on(TaskDropdownWithRecommendations.EVENT_SELECTED, (e, task: Task) => this.selectTask(task));
		$("#projectTimeStatusStartDate").on("change", () => this.taskDropdown.updateProjectTimeCount());

		this.config.panel.find(".taskQuery")
			.focus(e => this.currentFocusedField = $(e.target).attr("name"))
			.blur(() => this.currentFocusedField = null);

		this.config.panel.find("#projectTimeStatusBillable").change(e => {
			this.setBillable($(e.target).is(":checked"));
			this.updatePanelAppearanceForRecordingState();
			this.queueUpdate("projectTime");
		});

		this.projectTimeDescriptionTypeahead = new DescriptionTypeahead({
			element: this.config.panel.find("[name='projectTime.description']").closest(".input-group"),
			url: "filterProjectTimeDescriptions.json"
		});

		// listen to changes of the description typeahead suggestion selection
		$(this.projectTimeDescriptionTypeahead).on(DescriptionTypeahead.EVENT_CHANGED, () => {
			//get the updated description value and trigger a change event (for a correct height of the field)
			const description = this.config.panel.find("[name='projectTime.description']").change();

			// update the description field and toggle button
			this.updateDescriptionField("projectTime", this.taskDropdown.getSelectedData()?.descriptionRequired, description.val() as string);

			this.queueUpdate("projectTime");
		});

		this.projectTimeInfoPanel = new ProjectTimeInfoPanel(this.config.panel.find("#projectTimeInfoPanel"));

		this.switchResumeCombinedRecordingDialog.connect(this.taskDropdown);
	}

	/**
	 * Initialises actions for the button to toggle the description field
	 * Following actions are possible:
	 * - click on the add button: clear button is displayed and the description field is displayed and the cursor is in it
	 * - click on the clear button: add button is displayed and the description is cleared and field hidden
	 * @param type
	 */
	private initToggleDescriptionButton(type: RecordingType): void {
		const description = this.config.panel.find(`[name='${type}.description']`);

		// clear description button
		const clearButton = this.config.panel.find(`#${type}ClearDescriptionButton`)
			.click(() => {
				description.closest("[data-description-container]").hide();

				// clear field, reset description and queue update
				description.val("");
				this.queueUpdate(type);
				this.descriptionTypeaheadNeedReset(getModeForType(type));

				this.updateDescriptionButton(type, "add");
			});

		// add description button
		this.config.panel.find(`#${type}AddDescriptionButton`).click(() => {
			description.closest("[data-description-container]").show();

			// clear field, set cursor inside it (focus)
			// DEVNOTE: .change() is for a correct height of the field
			description.change().focus();

			this.updateDescriptionButton(type, "clear");
		});

		this.updateDescriptionButton(type, "add");

		// disable description button if note available, if blank enable field to allow toggle description field
		this.config.panel.find(`[name="${type}.description"]`).on("input", e => {
			const isDescriptionNotBlank = stringNotBlank($(e.currentTarget).val() as string);
			clearButton.prop("disabled", isDescriptionNotBlank);
		});
	}

	/**
	 * Updates the fields in the panel based on the given recording procedure.
	 *
	 * @param procedure The recording procedure which determines which fields to update.
	 */
	private updateFields(procedure: RecordingProcedure): void {
		if (isWorkingTimeIncluded(procedure)) {
			this.updateWorkingTimeFields();
		}
		if (isProjectTimeIncluded(procedure)) {
			this.updateProjectTimeFields();
		}

		// updates border and project time container background color based on recording state (billable, working time type, etc.)
		this.updatePanelAppearanceForRecordingState();

		this.updateEndTimeForNonRunningEntries();
		this.setEndDateTimeFieldsState(this.isEndDateTimeChangeAllowed());
		this.config.panel.find("[data-end-time-container]").toggle(this.isAnythingRunning());

		this.toggleDateFields();
		this.toggleTimeZoneFields();
	}

	/**
	 * Toggles visibility of the description button
	 * This depends on the <code>descriptionMode</code>
	 * <ul>
	 * 	<li>add: add button is shown ('+' title: Add note)</li>
	 * 	<li>clear: clear button is shown ('-' title: Remove note)</li>
	 * 	<li>required: disabled (!) required button is shown ('-' title: Note is required) only possible for project time</li>
	 * </ul>
	 * @param type working time or project time
	 * @param descriptionMode decides which button should be shown
	 * @param disableClearButton if true clear button gets disabled, otherwise enabled
	 */
	private updateDescriptionButton(type: RecordingType, descriptionMode: DescriptionToggleMode, disableClearButton: boolean = false): void {
		this.config.panel.find(`#${type}ClearDescriptionButton`).toggle(descriptionMode === "clear")
		this.config.panel.find(`#${type}AddDescriptionButton`).toggle(descriptionMode === "add");

		// disable clear button if required (task requires note)
		this.config.panel.find(`#${type}ClearDescriptionButton`).prop("disabled", disableClearButton);

		// currently the description can only be required for project time
		if (type === "projectTime") {
			this.config.panel.find(`#${type}RequiredDescriptionButton`).toggle(descriptionMode === "required");
		}
	}

	/**
	 * Updates flow state of status panel e.g. combined recording, only project time running, etc.
	 */
	private updateFlowState(): void {
		this.updateBreakResumeFlowState();

		// if active recording procedure is working time only or project time only, no additional flow state update is necessary, since other visibility is handled in jsp
		if (this.config.activeRecordingProcedure === RecordingProcedure.Both) {
			this.updateCombinedFlowState();
		}
	}

	/**
	 * Updates the break-resume hint flow state.
	 * This is relevant for all active procedures (including working-time-only).
	 */
	private updateBreakResumeFlowState(): void {
		// Indicates how a running break will be resumed (directly, with both records, or via switch dialog).
		const resumeAction = this.getResumeAction(this.getLastWorkingTime(), this.getLastProjectTime(), this.getRunningWorkingTime());

		// True when a break with position is currently running.
		const isBreakTimeWithPositionRunning = this.isBreakTimeWithPositionRunning();

		// True when break resume is only possible through the switch dialog fallback.
		const isResumeViaSwitchDialog = isBreakTimeWithPositionRunning && resumeAction === "switchDialog";

		// Show the break-resume hint only when break with position is active and not in switch-dialog fallback.
		const showPositionBreakResumeInfo = isBreakTimeWithPositionRunning && !isResumeViaSwitchDialog;

		// keep break resume info updated for all active procedures (including working-time-only),
		// otherwise the hint remains hidden when updateFlowState exits early
		this.config.panel.find("[data-break-resume-info]").toggle(showPositionBreakResumeInfo);
		const switchButton = this.config.panel.find("#combinedRecordingSwitchDropdown button[data-action='switch']");
		if (showPositionBreakResumeInfo) {
			switchButton.text(messages["combinedRecording.resume.with"]);
			this.updateBreakResumeInfo();
		} else {
			switchButton.text(messages["dialog.switch"]);
		}
	}

	/**
	 * Updates combined-only flow state handling (attendance/project-time related panel states).
	 */
	private updateCombinedFlowState(): void {
		/* flow state: handle state of either running working or project time */
		// Strict mode: attendance working time without project time should show an error state.
		const showOnlyWorkingTimeError = this.config.recordingMode === RecordingMode.STRICT && (this.isOnlyWorkingTimeRunning() && this.getRunningWorkingTime().workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime);

		// In both modes, project time without working time should show an error state.
		const showOnlyProjectTimeError = this.isOnlyProjectTimeRunning(); // relevant for both STRICT and COMBINED

		this.config.panel.find("#combinedRecordingStatusForm").toggle(!showOnlyWorkingTimeError && !showOnlyProjectTimeError);
		this.config.panel.find("[data-error-only-working-time-running]").toggle(showOnlyWorkingTimeError);
		this.config.panel.find(`[data-error-only-project-time-running="${this.config.recordingMode}"]`).toggle(showOnlyProjectTimeError);

		// Indicates how a running break will be resumed (directly, with both records, or via switch dialog).
		const resumeAction = this.getResumeAction(this.getLastWorkingTime(), this.getLastProjectTime(), this.getRunningWorkingTime());

		// True when a break with position is currently running.
		const isBreakTimeWithPositionRunning = this.isBreakTimeWithPositionRunning();

		// True when break resume is only possible through the switch dialog fallback.
		const isResumeViaSwitchDialog = isBreakTimeWithPositionRunning && resumeAction === "switchDialog";

		// Show the break-resume hint only when break with position is active and not in switch-dialog fallback.
		const showPositionBreakResumeInfo = isBreakTimeWithPositionRunning && !isResumeViaSwitchDialog;

		// Project time container is shown for attendance/project-time flows except break-resume info mode.
		const showProjectTimeContainer = (this.isAttendanceTimeSelected() || this.isProjectTimeRunning()) && !showPositionBreakResumeInfo;

		// Full project time inputs are shown only while something is running and project time container is active.
		const showProjectTimeFields = this.isAnythingRunning() && showProjectTimeContainer;

		this.config.panel.find("#includeProjectTimeFormGroup").toggle(this.config.recordingMode !== RecordingMode.STRICT && !showProjectTimeFields);
		this.config.panel.find("#projectTimeDateTimeGroup").toggle(showProjectTimeFields);
		if (showProjectTimeFields) {
			// if working or project time is running display all project time fields, otherwise the state 'include project time' toggle is visible
			this.config.panel.find("[data-project-time-field]").show();

			// reset the include pt switchery checkbox if unchecked and anything is running to true, so that the default value is true right after a stop/delete event, see TIMR-8776
			// DEVNOTE: only using the property checked on the input field does not work, this is one of the the cleanest workarounds https://stackoverflow.com/a/51181650
			if (!this.isProjectTimeIncluded()) {
				this.config.panel.find("#includeProjectTimeSwitchery").click();
			}
		}

		/* flow state: attendance/non-attendance working time is selected  */
		this.config.panel.find("#projectTimeContainer").toggle(showProjectTimeContainer);
		this.config.panel.find("[data-non-attendance-time]").toggle(!showProjectTimeContainer && !showPositionBreakResumeInfo && !isResumeViaSwitchDialog);
	}

	/**
	 * Updates the information related to resuming from a break time.
	 * This displays details of the records which are to be resumed (last working time and project time)
	 */
	private updateBreakResumeInfo(): void {
		const lastWorkingTime = this.getLastWorkingTime();
		const lastProjectTime = this.getLastProjectTime();

		// indicates how a running break will be resumed (directly, with both records, or via switch dialog).
		const resumeAction = this.getResumeAction(lastWorkingTime, lastProjectTime, this.getRunningWorkingTime());

		// always display the details of the last working time which are to be resumed
		this.config.panel.find("[data-resume-working-time-name]").text(lastWorkingTime?.workingTimeType?.name ?? "");

		// only display the project time info if the resume action includes resuming the project time
		this.config.panel.find("[data-resume-project-time-info]").toggle(resumeAction === "both");

		// display the details of the last project time which are to be resumed (if project time is included in the resume action)
		if (resumeAction === "both") {
			const taskName = lastProjectTime?.task?.name ?? "";
			const escapedBreadcrumbs = breadcrumbsWithoutName(lastProjectTime?.taskBreadCrumbs, taskName);
			this.config.panel.find("[data-resume-project-time-breadcrumbs]").html(escapedBreadcrumbs);
			this.config.panel.find("[data-resume-project-time-name]").text(taskName);
		}
	}

	/**
	 * Updates the visibility of duration and indicators.<br>
	 * The method toggles the visibility of the duration pulse and paused indicators based on the current status of working time and project time.
	 * <ul>
	 * <li>only working time is running -> working time pulse visible</li>
	 * <li>both working and project time are running -> project time pulse visible</li>
	 * <li>a record is paused -> pause icon for the paused recording is always visible</li>
	 * </ul>
	 */
	private updateDurationVisibility(): void {
		const isWorkingTimePaused = this.isWorkingTimePaused(),
			isProjectTimePaused = this.isProjectTimePaused(),
			isWorkingTimeRunning = this.isWorkingTimeRunning(),
			isProjectTimeRunning = this.isProjectTimeRunning(),
			isOnlyWorkingTimeTimeRunning = isWorkingTimeRunning && !isProjectTimeRunning;

		const workingTime = this.config.panel.find("div#workingTimeContainer");
		workingTime.find("[data-duration-pulse]").toggle(isWorkingTimeRunning)
		workingTime.find("[data-pulse]").toggle(!isWorkingTimePaused && isOnlyWorkingTimeTimeRunning || !isWorkingTimePaused && isProjectTimePaused); // visible when only wt is running or pt is paused
		workingTime.find("[data-paused]").toggle(isWorkingTimePaused);

		const projectTime = this.config.panel.find("div#projectTimeContainer")
		projectTime.find("[data-duration-pulse]").toggle(isProjectTimeRunning);
		projectTime.find("[data-pulse]").toggle(!isProjectTimePaused && !isOnlyWorkingTimeTimeRunning);
		projectTime.find("[data-paused]").toggle(isProjectTimePaused);
	}

	/**
	 * Initialize action buttons for "start", "stop", "delete" and "switch"<br>
	 *
	 * There are basically three scenarios for now (later there will be "PT only" possible):
	 * <ul>
	 * <li>Only working time is enabled -> the action buttons respond to click events directly (only apply to Working Time)</li>
	 * <li>The mode is STRICT -> the action buttons respond to click events directly</li>
	 * <li>Mode is COMBINED -> the action buttons have a dropdown menu depending on the state whether to apply both or just the project time</li>
	 * </ul>
	 */
	private initActionButtons(): void {
		// Add event listeners for "main" action e.g. "Switch" and/or if available "sub" actions inside dropdown "Switch Project Time"
		this.addActionButtonEventListeners();

		// in case of working or project time only, the action buttons are directly clickable (with Working Time action), further initializations regarding COMBINED or STRICT mode are not necessary
		if (this.config.activeRecordingProcedure === RecordingProcedure.WorkingTime) {
			this.config.panel.find("#combinedRecordingStartButton").click(() => this.shouldStartRecording(RecordingProcedure.WorkingTime));
			this.config.panel.find("#combinedRecordingStopButton").click(() => this.shouldStopRecording(RecordingProcedure.WorkingTime));
			this.config.panel.find("#combinedRecordingDeleteButton").click(() => this.shouldDeleteRecording(RecordingProcedure.WorkingTime));
			this.config.panel.find("#combinedRecordingSwitchBothButton").click(() => this.shouldSwitchRecording(RecordingProcedure.WorkingTime));
			this.config.panel.find("#combinedRecordingPauseButton").click(() => this.shouldPauseRecording(RecordingProcedure.WorkingTime));
			this.config.panel.find("#combinedRecordingResumeButton").click(() => this.shouldResumeRecording(RecordingProcedure.WorkingTime));
			return;
		} else if (this.config.activeRecordingProcedure === RecordingProcedure.ProjectTime) {
			this.config.panel.find("#combinedRecordingStartButton").click(() => this.shouldStartRecording(RecordingProcedure.ProjectTime));
			this.config.panel.find("#combinedRecordingStopButton").click(() => this.shouldStopRecording(RecordingProcedure.ProjectTime));
			this.config.panel.find("#combinedRecordingDeleteButton").click(() => this.shouldDeleteRecording(RecordingProcedure.ProjectTime));
			this.config.panel.find("#combinedRecordingSwitchBothButton").click(() => this.shouldSwitchRecording(RecordingProcedure.ProjectTime));
			this.config.panel.find("#combinedRecordingPauseButton").click(() => this.shouldPauseRecording(RecordingProcedure.ProjectTime));
			this.config.panel.find("#combinedRecordingResumeButton").click(() => this.shouldResumeRecording(RecordingProcedure.ProjectTime));
			return;
		}

		if (this.config.recordingMode === RecordingMode.STRICT) {
			this.config.panel.find("#combinedRecordingStartButton").click(() =>
				this.shouldStartRecording(this.getSelectedWorkingTimeType().category === WorkingTimeTypeCategory.AttendanceTime ? RecordingProcedure.Both : RecordingProcedure.WorkingTime));
			this.config.panel.find("#combinedRecordingStopButton").click(() =>
				this.shouldStopRecording(this.getSelectedWorkingTimeType().category === WorkingTimeTypeCategory.AttendanceTime ? RecordingProcedure.Both : RecordingProcedure.WorkingTime));
			this.config.panel.find("#combinedRecordingDeleteButton").click(() =>
				// if working time and project time are running, working time type is then changed to a non-attendance type (not allowed state), delete both must still be possible
				this.shouldDeleteRecording(this.isProjectTimeRunning() ? RecordingProcedure.Both : RecordingProcedure.WorkingTime));

			// switch
			this.config.panel.find("#combinedRecordingSwitchBothButton").click(() => this.shouldSwitchRecording(RecordingProcedure.Both));
			this.config.panel.find("#combinedRecordingSwitchProjectTimeButton").click(() => this.shouldSwitchRecording(RecordingProcedure.ProjectTime));

		} else {
			// switch
			this.config.panel.find("#combinedRecordingSwitchBothButton").click(() =>
				// show project time start option in toggle dialog, depending on whether project time is running
				this.shouldSwitchRecording(this.isProjectTimeRunning() ? RecordingProcedure.Both : RecordingProcedure.WorkingTime)
			);
			this.config.panel.find("#combinedRecordingSwitchProjectTimeButton").click(() => this.shouldSwitchRecording(RecordingProcedure.ProjectTime));

			// start
			this.config.panel.find("#combinedRecordingStartButton").click(() =>
				// start both or just working time based on value selection of "include project time" (switchery)
				this.shouldStartRecording(this.isProjectTimeIncluded() && this.isAttendanceTimeSelected() ? RecordingProcedure.Both : RecordingProcedure.WorkingTime)
			);
			this.config.panel.find("#combinedRecordingStartProjectTimeButton").click(() => this.shouldStartRecording(RecordingProcedure.ProjectTime));

			// stop
			this.config.panel.find("#combinedRecordingStopBothButton").click(() => this.shouldStopRecording(RecordingProcedure.Both));
			this.config.panel.find("#combinedRecordingStopProjectTimeButton").click(() => this.shouldStopRecording(RecordingProcedure.ProjectTime));

			// delete
			this.config.panel.find("#combinedRecordingDeleteBothButton").click(() => this.shouldDeleteRecording(RecordingProcedure.Both));
			this.config.panel.find("#combinedRecordingDeleteProjectTimeButton").click(() => this.shouldDeleteRecording(RecordingProcedure.ProjectTime));
		}

		// pause
		this.config.panel.find("#combinedRecordingPauseButton").click(() => this.shouldPauseRecording(RecordingProcedure.Both));
		this.config.panel.find("#combinedRecordingPauseWorkingTimeButton").click(() => this.shouldPauseRecording(RecordingProcedure.WorkingTime));
		this.config.panel.find("#combinedRecordingPauseProjectTimeButton").click(() => this.shouldPauseRecording(RecordingProcedure.ProjectTime));

		// resume
		this.config.panel.find("#combinedRecordingResumeButton").click(() => this.shouldResumeRecording(RecordingProcedure.Both));
		this.config.panel.find("#combinedRecordingResumeWorkingTimeButton").click(() => this.shouldResumeRecording(RecordingProcedure.WorkingTime));
		this.config.panel.find("#combinedRecordingResumeProjectTimeButton").click(() => this.shouldResumeRecording(RecordingProcedure.ProjectTime));
	}

	/**
	 * Adds event listeners to action buttons for handling specific recording actions
	 * such as start, delete, switch, stop.
	 *
	 * There basically two options for the action buttons:
	 * - Distinction (With Hover):	Generally available for "delete", "switch" and "stop" actions if both working and a project time are available.
	 * 								If these conditions are met; running project time running matters:
	 * 									-> Running: the button has a hover state to open a context menu with sub actions (only pt or both)
	 * 									-> Not running: no context menu is available and a click on the button starts/stops (wt) action immediately
	 * - No distinction state: 		State is present if:
	 * 									-> "start": always a non-hover state, since no context menu is necessary
	 * 									-> "delete", "switch" and "stop"; available if:
	 * 										-> working time OR project time is exclusive enabled
	 * 										-> both are available: no project time is running
	 *
	 * @return {void} This method does not return any value.
	 */
	private addActionButtonEventListeners(): void {
		this.config.panel.find("div[id^='combinedRecording'][id$='Dropdown']")
			// handle the non-distinction state; that means there is no context menu available on hover and a click on the button directly triggers the action
			.click(e => {
				if (this.config.activeRecordingProcedure !== RecordingProcedure.Both || !this.isProjectTimeRunning()) {
					e.stopImmediatePropagation();

					// If activeRecordingProcedere is BOTH, a click should affect the working time. If activeRecordingProcedere is WT or PT, it refers to the working time or project time, respectively
					const procedureForAction = this.config.activeRecordingProcedure === RecordingProcedure.ProjectTime ? RecordingProcedure.ProjectTime : RecordingProcedure.WorkingTime;
					switch ($(e.target).attr("data-action") as CombinedRecordingButtonAction) {
						case "delete":
							this.shouldDeleteRecording(procedureForAction);
							break;
						case "switch":
							this.shouldSwitchRecording(procedureForAction);
							break;
						case "stop":
							this.shouldStopRecording(procedureForAction);
							break;
					}
				} else {
					// if project time is running: avoid any action on dropdown button click (also for loading state)
					$(e.currentTarget).off();

					// add dropdown listeners again (with timeout to avoid immediate event trigger again)
					setTimeout(() => this.addActionButtonEventListeners(), 500);
				}
			})
			// handle distinction state; that means context menu is opened on mouse over (hover)
			.on("mouseover", e => {
				const actionDropdown = $(e.currentTarget);

				// Check if the end time row container is disabled (prevents the context menu from opening when end time editing is not allowed)
				const isEndTimeRowDisabled = actionDropdown.closest(".end-time-row").is(":disabled");

				// Check if the dropdown toggle button itself is disabled (ensures the context menu doesn't open for disabled action buttons)
				const isActionButtonDisabled = actionDropdown.find("[data-toggle='dropdown']").is(":disabled, .disabled");

				// Show context menu on hover only when all conditions are met:
				// - Both working time and project time are active
				// - Project time is currently running (only in this case distinction between main action and dropdown actions is necessary)
				// - Neither the end time row nor the action button is disabled (after actions/while requests)
				const showContextMenuOnHover =
					this.config.activeRecordingProcedure === RecordingProcedure.Both
					&& this.isProjectTimeRunning()
					&& !isEndTimeRowDisabled
					&& !isActionButtonDisabled;

				actionDropdown.toggleClass("open", showContextMenuOnHover);
			})
			.on("mouseout", e => {
				// close context menu on mouse out (hover out)
				$(e.currentTarget).removeClass("open");
			});
	}

	/**
	 * Returns last recording for given type
	 * @param type working time or project time
	 */
	private getLastRecording(type: RecordingType): Record | undefined {
		switch (type) {
			case "workingTime":
				return this.currentStatus?.workingTimeStatus?.lastWorkingTime;
			case "projectTime":
				return this.currentStatus?.projectTimeStatus?.lastProjectTime;
		}
	}

	/**
	 * Returns custom field definitions for given type
	 * @param type working time or project time
	 */
	private getCustomFieldDefinitions(type: RecordingType): CustomFieldDefinition[] {
		switch (type) {
			case "workingTime":
				return this.config.customFieldDefinitionsWorkingTime;
			case "projectTime":
				return this.config.customFieldDefinitionsProjectTime;
		}
	}

	/**
	 * Returns the custom field comboboxes for the given recording type
	 * @param type The type of recording (working time or project time)
	 */
	private getCustomFieldComboboxes(type: RecordingType): Combobox<CustomFieldOption>[] {
		switch (type) {
			case "workingTime":
				return this.workingTimeCustomFieldComboboxes;
			case "projectTime":
				return this.projectTimeCustomFieldComboboxes;
		}
	}

	/**
	 * Returns running working time, if not available <code>null</code>
	 */
	private getRunningWorkingTime(): RunningWorkingTime {
		return this.currentStatus?.workingTimeStatus?.runningWorkingTime ?? null;
	}

	/**
	 * Returns last project time, if not available <code>null</code>
	 */
	private getLastProjectTime(): ProjectTime {
		return this.currentStatus?.projectTimeStatus?.lastProjectTime ?? null;
	}

	/**
	 * Returns last working time, if not available <code>null</code>
	 */
	private getLastWorkingTime(): WorkingTime {
		return this.currentStatus?.workingTimeStatus?.lastWorkingTime ?? null;
	}

	/**
	 * Returns running project time, if not available <code>null</code>
	 */
	private getRunningProjectTime(): RunningProjectTime {
		return this.currentStatus?.projectTimeStatus?.runningProjectTime ?? null;
	}

	/**
	 * Initialize date/time field listeners taking into account date time changeable permissions.
	 *
	 * Fields that affect both times are only available if working and project time are allowed, such as the end time field.
	 * The working time start time field is similar to the end time field, but a more specific case:
	 * if nothing is running, this time applies to both entries, meaning that after the start, this time is also applied to the project time.
	 * Therefore, the state of this field is updated with actions using setStartDateTimeFieldsState based on the flow state and date time changeable of both entries.
	 */
	private initDateFieldListeners(): void {
		if (this.isWorkingTimeEnabled() && isWorkingTimeDateTimeChangeAllowed()) {
			this.initStartDateFieldListeners("workingTime");
		}
		if (this.isProjectTimeEnabled() && isProjectTimeDateTimeChangeAllowed()) {
			this.initStartDateFieldListeners("projectTime");
		}

		this.initEndDateFieldListeners();
	}

	/**
	 * Initialize end date/time field listeners for change field detection and date/time formatting.
	 * This includes event handling for context menu selection for end time field
	 */
	private initEndDateFieldListeners(): void {
		this.config.panel.find(`input[name='combinedRecording.endDate'], input[name='combinedRecording.endTime'], input[name='combinedRecording.endTimeZone']`)
			.on("input", e => {
				const input = $(e.target);
				this.changedFields.add(input.attr("name"));
			});

		this.config.panel.find(`input[name='combinedRecording.endDate'], input[name='combinedRecording.endTimeZone']`)
			.focus(e => this.currentFocusedField = $(e.target).attr("name"))
			.blur(() => this.currentFocusedField = null);

		this.config.panel.find(`input[name='combinedRecording.endTime']`)
			.focus(e => this.currentFocusedField = $(e.target).attr("name"))
			.blur(e => {
				this.currentFocusedField = null;
				normalizeTimeInput($(e.target));
			});

		const endTimeDropdownMenu = this.config.panel.find(`[name='combinedRecording.endTime']`).closest(".dropdown").find(".dropdown-menu");
		this.config.panel.find(`[name='combinedRecording.endTime']`)
			.focus(() => endTimeDropdownMenu.show().find(".setEndToNow > a > span").text(`(${formatTime(Temporal.Now.zonedDateTimeISO())})`))
			.keydown(e => {
				if (e.key === "Tab" || e.key === "Enter") {
					endTimeDropdownMenu.hide();
				}
			}).focusout(e => {
			if (!$(e.relatedTarget).is("a.shift-time-entries")) {
				endTimeDropdownMenu.hide();
			}
		});

		this.config.panel.find(".setEndToNow")
			.prop("disabled", false)
			.click(() => {
				const now = Temporal.Now.zonedDateTimeISO();
				this.config.panel.find("input[name='combinedRecording.endDate']").val(formatDate(now));
				this.config.panel.find("input[name='combinedRecording.endTime']").val(formatTime(now));
				this.config.panel.find("input[name='combinedRecording.endTimeZone']").val(now.offset);

				// setting end to "now" means end is no longer user-edited/dirty
				this.resetChangedEndDateTimeState();

				endTimeDropdownMenu.hide();
			});

		this.config.panel
			.find(`
				input[name='combinedRecording.endDate'],
				input[name='combinedRecording.endTime'],
				input[name='combinedRecording.endTimeZone']`)
			.on("input", () => this.changedEnd = true);

		this.config.panel.find("input[name='combinedRecording.endDate']")
			.on("apply.daterangepicker", () => {
				this.changedEnd = true;
				this.changedFields.add("combinedRecording.endDate");
			});
	}

	/**
	 * Resets changed state for shared end date/time fields.
	 * Used when end is explicitly set to "now" or after successful actions.
	 */
	private resetChangedEndDateTimeState(): void {
		this.changedFields = this.filterSet(field =>
			field !== "combinedRecording.endDate" &&
			field !== "combinedRecording.endTime" &&
			field !== "combinedRecording.endTimeZone");
		this.changedEnd = false;
	}

	/**
	 * Initialize end date/time field listeners for change field detection and date/time formatting for given type
	 * This includes event handling for context menu selection for start time fields
	 * @param type working time or project time
	 */
	private initStartDateFieldListeners(type: RecordingType): void {
		this.config.panel.find(`input[name='${type}.startDate'], input[name='${type}.breakTime'], input[name='${type}.startTimeZone']`)
			.focus(e => this.currentFocusedField = $(e.target).attr("name"))
			.blur(() => this.currentFocusedField = null);
		this.config.panel.find(`input[name='${type}.startTime']`)
			.focus(e => this.currentFocusedField = $(e.target).attr("name"))
			.blur(e => {
				this.currentFocusedField = null;
				normalizeTimeInput($(e.target));
			});
		this.config.panel.find(`input[name='${type}.startDate'], input[name='${type}.startTime'], input[name='${type}.startTimeZone'], input[name='${type}.breakTime']`)
			.on("input", e => {
				const input = $(e.target);
				this.changedFields.add(input.attr("name"));
				this.queueUpdate(type);
			});

		const startTimeDropdownMenu = this.config.panel.find(`[name='${type}.startTime']`).closest(".dropdown").find(".dropdown-menu");
		this.config.panel.find(`[name='${type}.startTime']`)
			.focus(() => {
				const now = Temporal.Now.zonedDateTimeISO();
				const lastRecording = this.getLastRecording(type);
				const setNow = startTimeDropdownMenu.find(".setStartToNow");
				const setLastEnd = startTimeDropdownMenu.find(".setStartToLastEnd");

				setNow.find("span").text(`(${formatTime(now)})`);

				// only display last end suggestion if it is the same day of today
				const lastEnd = parseISOZonedDateTime(lastRecording?.end);
				const showLastEnd = isSameDay(lastEnd, now);
				setLastEnd.toggle(showLastEnd);
				if (showLastEnd) {
					setLastEnd.find("span").text(`(${formatTime(lastEnd)})`);
				}
				startTimeDropdownMenu.show();
			})
			.keydown(e => {
				if (e.key === "Tab" || e.key === "Enter") {
					startTimeDropdownMenu.hide();
				}
			})
			.focusout(e => {
				if (!$(e.relatedTarget).is("a.shift-time-entries")) {
					startTimeDropdownMenu.hide();
				}
			});

		this.config.panel.find(`#${type}Container .setStartToLastEnd`)
			.prop("disabled", false)
			.click(() => {
				const lastRecording = this.getLastRecording(type);
				if (lastRecording?.end) {
					const startDate = parseISOZonedDateTime(lastRecording.end);

					this.config.panel.find(`input[name='${type}.startDate']`).val(formatDate(startDate));
					this.config.panel.find(`input[name='${type}.startTime']`).val(formatTime(startDate));

					this.changedFields.add(`${type}.startDate`);
					this.changedFields.add(`${type}.startTime`);

					this.setStartDateTimeChanged(type);
					this.toggleDateFields();
					this.toggleTimeZoneFields();
					this.queueUpdate(type);
				}
				startTimeDropdownMenu.hide();
			});

		this.config.panel.find(`#${type}Container .setStartToNow`).prop("disabled", false).click(() => {
			const now = Temporal.Now.zonedDateTimeISO();
			this.config.panel.find(`input[name='${type}.startDate']`).val(formatDate(now));
			this.config.panel.find(`input[name='${type}.startTime']`).val(formatTime(now));
			this.changedFields = this.filterSet(field => field !== `${type}.startDate` && field !== `${type}.startTime`);
			this.toggleDateFields();
			this.toggleTimeZoneFields();
			this.queueUpdate(type);
			startTimeDropdownMenu.hide();
		});

		this.config.panel
			.find(`
						input[name='${type}.startDate'],
						input[name='${type}.startTime'],
						input[name='${type}.startTimeZone']`)
			.on("input", () => this.setStartDateTimeChanged(type));

		this.config.panel.find(`input[name='${type}.startDate']`).on("apply.daterangepicker", () => {
			this.setStartDateTimeChanged(type);
			if (!this.changedFields.has(`${type}.startDate`)) {
				this.changedFields.add(`${type}.startDate`);
				this.queueUpdate(type);
			}
		});
	}

	/**
	 * Initialize events for custom fields
	 * @param type working time or project time
	 */
	private initCustomFields(type: RecordingType): void {
		if (this.getCustomFieldDefinitions(type)?.length !== 0) {
			this.config.panel.find(`input[name^="${type}.customField"]`).on("input", () => this.queueUpdate(type));
			this.config.panel.find(`[id^="${type}.customField"]:checkbox`).change(e => {
				const input = $(e.target);
				const hiddenInput = this.config.panel.find(`input[name="${type}.${input.val() as string}"]`);
				if (hiddenInput) {
					hiddenInput.val(String(input.is(":checked")));
					this.queueUpdate(type);
				}
			});
			for (const definition of this.getCustomFieldDefinitions(type)) {
					if (definition.fieldType === CustomFieldsForRecordType.Options && definition.fieldName) {
						const customFieldComboboxItems: CustomFieldOption[] = [];
						for (const item of definition.optionsList ?? []) {
							customFieldComboboxItems.push({id: item});
						}
					const customFieldCombobox = new Combobox<CustomFieldOption>({
						items: customFieldComboboxItems,
						formatter: (item: CustomFieldOption) => item.id,
						idElement: this.config.panel.find(`[name="${type}.customField${definition.fieldNumber}"]`),
						filterElement: this.config.panel.find(`[id^="${type}.custom"][id$="Field${definition.fieldNumber}FilterStatusPanel"]`),
						caretElement: this.config.panel.find(`[id="${type}.customField${definition.fieldNumber}Caret"]`),
						clearable: !definition.mandatory
					});

					if (type === "workingTime") {
						$(customFieldCombobox).on(Combobox.EVENT_CHANGED, () => {
							if (isWorkingTimeIncluded(this.getRecordingProcedure())) {
								this.queueUpdate("workingTime")
							}
						});
						this.workingTimeCustomFieldComboboxes.push(customFieldCombobox);
					} else {
						$(customFieldCombobox).on(Combobox.EVENT_CHANGED, () => {
							if (isProjectTimeIncluded(this.getRecordingProcedure())) {
								this.queueUpdate("projectTime")
							}
						});
						this.projectTimeCustomFieldComboboxes.push(customFieldCombobox);
					}
				}
			}
		}
	}

	/**
	 * Queues an update if necessary based on the current recording status and type.
	 * This is used to sync the recording updates when fields change.
	 * @param type the type of the recording that has got a change in a field (e.g., "workingTime" or "projectTime")
	 * @private
	 */
	private queueUpdate(type: RecordingType): void {
		// Validates if an update is necessary for the given type (recording) based on the current running recordings
		// E.g., if only working time is running, no update is necessary for a change in the non-running project time
		const shouldQueueUpdateForRunningRecording = (): boolean => {
			const affectedProcedure = getModeForType(type);
			const runningProcedure = this.getRecordingProcedure();

			// validate if any recording is running and if the affected recording (changed property) is running
			return !!runningProcedure && (
				isWorkingTimeIncluded(affectedProcedure) && isWorkingTimeIncluded(runningProcedure) ||
				isProjectTimeIncluded(affectedProcedure) && isProjectTimeIncluded(runningProcedure)
			);
		}

		if (shouldQueueUpdateForRunningRecording()) {
			if (this.updateTimeout && this.updateTransaction === null) {
				clearTimeout(this.updateTimeout);
				this.updateTimeout = null;
			}
			this.waitForUpdateToFinish(() => this.updateTimeout = setTimeout(() => this.doSendUpdate(), CombinedRecordingStatusPanel.UPDATE_TIMEOUT_MS));
		}
	}

	/**
	 * Wait for update if necessary, otherwise schedule timeout
	 * @param callback
	 */
	private waitForUpdateToFinish(callback: () => void): void {
		if (!this.updateTimeout && this.updateTransaction === null) {
			callback.call(this);
		} else if (!this.waitForUpdateTimeout) {
			this.waitForUpdateTimeout = setTimeout(() => {
				this.waitForUpdateTimeout = null;
				this.waitForUpdateToFinish(callback);
			}, CombinedRecordingStatusPanel.UPDATE_TIMEOUT_PAUSE_MS);
		}
	}

	/**
	 * Clear current time out
	 */
	private clearTimeout(): void {
		clearTimeout(this.updateTimeout);
		this.updateTimeout = null;
	}


	/**
	 * Fetches the current recording status from the server and updates the panel.
	 * If no working time or project time is running, the respective start field is reset to "now"
	 * (provided it has not been manually changed) before the fetch so that a new recording
	 * always defaults to the current time.
	 * <p>
	 * Skipped entirely when either a debounce timeout for a pending user edit is active or an update
	 * request is already in-flight — both guards prevent a stale fetch response from overwriting
	 * unsent or in-transit user changes.
	 *
	 * @param background When true, network errors are swallowed silently (no error dialog).
	 *                   Suitable for background polling so that a transient server hiccup does not
	 *                   interrupt the user (see TIMR-8416).
	 */
	public fetchRecordingStatus(background: boolean = false): void {
		if (!this.isWorkingTimeRunning()) {
			this.setStartToNowIfNotChanged("workingTime");
		}
		if (!this.isProjectTimeRunning()) {
			this.setStartToNowIfNotChanged("projectTime");
		}

		// Do not start a new fetch while a pending edit timeout or an in-flight update transaction
		// is active — the response from our own update will refresh the panel instead.
		if (this.updateTimeout === null && this.updateTransaction === null) {
			api("timeline/getCombinedRecordingStatus.json", background)
				.get({localTimeZone: getCurrentISOOffset()})
				.done((status: CombinedRecordingStatus | null) => {
					if (this.reloadIfRecordingUnavailable(status)) {
						return;
					}

					// update status of combined recording (always use both procedures so that the panels correctly display a correct state)
					this.updateStatus(status, this.config.activeRecordingProcedure);

					// The timeline is not reloaded with every request, but only if the range contains the running recording and it has changed (version).
					// In this case, a reload is carried out to match the combined recording panel. This is done with TimelinePersonal#reloadTimelineIfNecessary
					this.updateTimeline();
				});
		}
	}

	/**
	 * Is called when an update is to be carried out. When it is carried out depends on whether an update is already running
	 */
	private doSendUpdate(): void {
		if (this.updateTimeout) {
			this.clearTimeout();
		}
		if (this.isAnythingRunning()) {
			this.updateTransaction = this.sendUpdate();
			void this.updateTransaction.always(() => this.updateTransaction = null);
		}
	}

	/**
	 * Returns the recording procedure for <code>currentStatus</code>
	 * This is determined by the status of whether there is a running recording or not.
	 * If there is no running recording at all, null is returned.
	 */
	private getRecordingProcedure(): RecordingProcedure {
		if (this.isWorkingTimeRunning() && this.isProjectTimeRunning()) {
			return RecordingProcedure.Both;
		} else if (this.isWorkingTimeRunning()) {
			return RecordingProcedure.WorkingTime;
		} else if (this.isProjectTimeRunning()) {
			return RecordingProcedure.ProjectTime;
		}
		return null;
	}

	/**
	 * Sends update to endpoint about running entries
	 */
	private sendUpdate(): JQuery.jqXHR<CombinedRecordingStatus> {
		const form = this.config.panel.find("#combinedRecordingStatusForm");
		const end = this.getEnd();

		if (this.isWorkingTimeEnabled()) {
			const start = this.getStart("workingTime");
			this.config.panel.find("[name='workingTime.start']").val(getISOZonedDateTime(start));
			this.config.panel.find("[name='workingTime.end']").val(getISOZonedDateTime(end));
		}
		if (this.isProjectTimeEnabled()) {
			const start = this.getStart("projectTime");
			this.config.panel.find("[name='projectTime.start']").val(getISOZonedDateTime(start));
			this.config.panel.find("[name='projectTime.end']").val(getISOZonedDateTime(end));
		}

		const now = Temporal.Now.zonedDateTimeISO();
		const params: RequestParam[] = this.config.panel.find("#combinedRecordingStatusForm").serializeArray();
		params.push({name: "procedure", value: this.getRecordingProcedure()});
		params.push({name: "localTimeZone", value: now.offset});
		params.push({name: "timestamp", value: now.epochMilliseconds.toString()});

		if (this.isWorkingTimeEnabled()) {
			// if working time is included, add changed flag for working time start
			params.push({name: "workingTime.changed", value: this.changedWorkingTimeStart});
		}
		if (this.isProjectTimeEnabled()) {
			params.push({name: "projectTime.changed", value: this.changedProjectTimeStart});
		}

		return api("timeline/updateCombinedRecording.json")
			.post(params)
			.done((status: CombinedRecordingStatus | null) => {
				if (this.reloadIfRecordingUnavailable(status)) {
					return;
				}

				FormUtil.removeAllFeedback(form);
				const procedure = this.getRecordingProcedure();

				// clear start time fields after update, exclude end date time fields to keep it's state (to prevent the end time from continuing if it has been changed)
				// DEVNOTE: In order to remain consistent with the existing old panels, only start fields (time, date, timezone) are cleared after the update
				// 			(it is not entirely clear why not all of them are deleted, but it is important that the panels behave in the same way, see TIMR-9444)
				this.changedFields = this.filterSet((field: string) =>
					!(isWorkingTimeIncluded(procedure) && field.startsWith("workingTime.start")) &&
					!(isProjectTimeIncluded(procedure) && field.startsWith("projectTime.start"))
				);

				if (isWorkingTimeIncluded(procedure) && status.workingTimeStatus?.runningWorkingTime) {
					this.config.panel.find("[name='workingTime.version']").val(status.workingTimeStatus.runningWorkingTime.version);
				}
				if (isProjectTimeIncluded(procedure) && status.projectTimeStatus?.runningProjectTime) {
					this.config.panel.find("[name='projectTime.version']").val(status.projectTimeStatus.runningProjectTime.version);
				}

				// update status
				this.updateStatus(status, procedure);

				// refresh timeline with current values that updated recordings are displayed in the recording panel (only if the running recording is in selected date range)
				this.updateTimeline();
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			});
	}

	/**
	 * Validates security status for given procedure
	 * @param procedure
	 */
	private isSecurityStatusValid(procedure: RecordingProcedure): boolean {
		if (procedure === RecordingProcedure.Both && !(SecurityStatus.isAddWorkingTimeAllowed() || SecurityStatus.isAddProjectTimeAllowed())) {
			BootstrapModal.info({
				title: "account.expired.both.title",
				message: "account.expired.both",
				escapeText: false
			});
			return false;
		} else if (isWorkingTimeIncluded(procedure) && !SecurityStatus.isAddWorkingTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.workingtime.title",
				message: "account.expired.workingtime",
				escapeText: false
			});
			return false;
		} else if (isProjectTimeIncluded(procedure) && !SecurityStatus.isAddProjectTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.projecttime.title",
				message: "account.expired.projecttime",
				escapeText: false
			});
			return false;
		}
		return true;
	}

	/**
	 * Validates the start time for the given recording procedure.
	 * Ensures that the security status is valid and the start date and time are correct.
	 * If the start date and time are invalid, an error modal is displayed.
	 *
	 * @param procedure The recording procedure to validate.
	 * @returns True if the start time is valid, false otherwise.
	 */
	private validateStart(procedure: RecordingProcedure): boolean {
		// check if the security status is valid for the given procedure
		if (!this.isSecurityStatusValid(procedure)) {
			return false;
		}

		// validate the stop date and time (always validate both independent of procedure to ensure correct data is transmitted to server)
		const stopDateTimeValid =
			(!this.isWorkingTimeEnabled() || this.validateStartForType("workingTime")) &&
			(!this.isProjectTimeEnabled() || this.validateStartForType("projectTime"));

		// display an error modal if the stop date and time are invalid
		if (!stopDateTimeValid) {
			BootstrapModal.error({message: "invalid.start.datetime"});
		}

		// return the validation result
		return stopDateTimeValid;
	}

	/**
	 * Validates the stop time for the given recording procedure.
	 * Ensures that the security status is valid and the stop date and time are correct.
	 * If the stop date and time are invalid, an error modal is displayed.
	 *
	 * @param procedure The recording procedure to validate.
	 * @returns True if the stop time is valid, false otherwise.
	 */
	private validateStop(procedure: RecordingProcedure): boolean {
		// check if the security status is valid for the given procedure
		if (!this.isSecurityStatusValid(procedure)) {
			return false;
		}

		// validate the stop date and time (always validate both records independent of procedure to ensure correct data is transmitted to server)
		const startDateTimeValid =
			(!this.isWorkingTimeEnabled() || this.validateStopForType("workingTime")) &&
			(!this.isProjectTimeEnabled() || this.validateStopForType("projectTime"));

		// display an error modal if the stop date and time are invalid
		if (!startDateTimeValid) {
			BootstrapModal.error({message: "invalid.end.datetime"});
		}

		// return the validation result
		return startDateTimeValid;
	}

	/**
	 * Validates the start time for the given recording type.
	 * Ensures that time format is correct, the start date and time are not null, and the time zone is valid.
	 * If all validations pass, it sets the formatted start date and time.
	 *
	 * @param type The type of recording (working time or project time).
	 * @returns True if the start time is valid, false otherwise.
	 */
	private validateStartForType(type: RecordingType): boolean {
		// fix the time format for the start time input field
		normalizeTimeInput(this.config.panel.find(`input[name='${type}.startTime']`));

		// get the start date and time
		const dateTime = this.getStart(type);
		if (dateTime == null) {
			return false;
		}

		// validate the time zone format
		const timeZone = this.config.panel.find(`input[name='${type}.startTimeZone']`).val() as string;
		if (!globals.timeZonePattern.test(timeZone)) {
			return false;
		}

		// set the formatted start date and time with the time zone
		this.config.panel.find(`input[name='${type}.start']`).val(getISOZonedDateTime(dateTime));

		// date and time are valid
		return true;
	}

	/**
	 * Validates the stop time for the given recording type.
	 * Ensures that the start time is valid, the time format is correct, the end date and time are not null, and the time zone is valid.
	 * If all validations pass, it returns true.
	 *
	 * @param type The type of recording (working time or project time).
	 * @returns True if the stop time is valid, false otherwise.
	 */
	private validateStopForType(type: RecordingType): boolean {
		// validate the start time first
		if (!this.validateStartForType(type)) {
			return false;
		}

		// fix the time format for the end time input field
		normalizeTimeInput(this.config.panel.find("input[name='combinedRecording.endTime']"));

		// get the end date and time
		const endDateTime = this.getEnd();
		if (endDateTime === null) {
			return false;
		}

		// validate the time zone format
		const endTimeZone = this.config.panel.find("input[name='combinedRecording.endTimeZone']").val() as string;
		if (!globals.timeZonePattern.test(endTimeZone)) {
			return false;
		}

		// date and time are valid
		return true;
	}

	/**
	 * Action for autocorrect dialog, which popup if date of start and end are different for given type.
	 * If the end time is earlier than the start time, it increments the end date by one day.
	 *
	 * @param type working time or project time
	 */
	private autoCorrectEndDateTime(type: RecordingType): void {
		// mark the end date field as changed
		this.changedFields.add("combinedRecording.endDate");

		/* set the end date to match the start date
		 * e.g. start: 6.11/11:00 -> end: 7.11/12:00 (25:00h)
		 *		-> end date is set to start date: 6.11/13:00 -> end: 6.11/12:00 (1:00h)
		 */
		const startDateTime = this.getStart(type);
		this.config.panel.find("[name='combinedRecording.endDate']").val(formatDate(startDateTime));

		/* start and end date are the same at this point, if end time is before start time, the end date is incremented by one day
		 * e.g. start: 5.11/13:00 -> end: 7.11/12:00 (47:00h)
		 *		autocorrect in the line above changes dates to the following: start: 5.11/13:00 -> end: 5.11/12:00 which is not possible because end time is before start time
		 * 		-> end date is increased by one day to: 5.11/13:00 -> end: 6.11/12:00 (23:00h)
		 */
		const endDateTime = this.getEnd();
		if (endDateTime !== null && startDateTime !== null && Temporal.ZonedDateTime.compare(endDateTime, startDateTime) < 0) {
			this.config.panel.find("[name='combinedRecording.endDate']").val(formatDate(endDateTime.add({days: 1})));
		}

		// Consider autocorrected times as changed
		this.changedEnd = true;
	}

	/**
	 * Prepares stop recording which includes validation, autocorrection dialog. Central validation for actions that include stop recording (switch/quick switch/stop/pause/resume)
	 * If succeeded the stop recording will be executed
	 * @param procedure working time, project time or both
	 * @param success method to call if validation completed
	 */
	private prepareStopRecording(procedure: RecordingProcedure, success: () => void): void {
		// end time may be edited if the date and time of at least one of the records are modifiable. Therefore, it is required to validate that the action is allowed for the potentially edited end time.
		if (!this.isActionAllowedForEditedEndTime(procedure)) {
			return;
		}

		if (!this.validateStop(procedure)) {
			return;
		}

		// check if the duration of the recording exceeds 24 hours for working time and project time
		const isDurationOver24Hours = (type: RecordingType) => {
			const end = this.getEnd();
			const start = this.getStart(type);
			return end !== null && start !== null && start.until(end).total({unit: 'hours'}) > 24;
		};
		const isWorkingTimeOver24Hours = isWorkingTimeIncluded(procedure) && isDurationOver24Hours("workingTime"),
			isProjectTimeOver24Hours = isProjectTimeIncluded(procedure) && isDurationOver24Hours("projectTime");
		if (isWorkingTimeOver24Hours || isProjectTimeOver24Hours) {
			this.showAutoCorrectDialog(procedure, isWorkingTimeOver24Hours, isProjectTimeOver24Hours, success);
			return;
		}
		success();
	}

	/**
	 * Displays a confirmation dialog for autocorrecting the end date/time if the duration exceeds 24 hours.
	 * An automatic correction of both records is only possible if both are above 24 hours and on the same date.
	 * Otherwise a warning is displayed, if the user is sure to save these records
	 *
	 * @param procedure working time, project time or both
	 * @param autoCorrectWorkingTime Indicates if the working time duration is over 24 hours and end date/time should be autocorrected.
	 * @param autoCorrectProjectTime Indicates if the project time duration is over 24 hours and end date/time should be autocorrected.
	 * @param success The callback function to execute if the user confirms the action.
	 */
	private showAutoCorrectDialog(procedure: RecordingProcedure, autoCorrectWorkingTime: boolean, autoCorrectProjectTime: boolean, success: () => void): void {
		/* an auto-correction of both records is only possible if both are above 24 hours and on the same date
		 * otherwise it is not guaranteed, that end date is after both start dates after the auto-correction
		 * for this case there is warning is displayed (without option to auto-correct)
		 */
		const autoCorrectNotAvailable = procedure === RecordingProcedure.Both && (
			!isSameDay(this.getStart("workingTime"), this.getStart("projectTime")) ||
			!autoCorrectWorkingTime ||
			!autoCorrectProjectTime
		);

		// determine the appropriate message based on the auto-correction conditions
		let message: string;
		if (autoCorrectNotAvailable) {
			message = "combinedRecording.warning.message.BOTH";
		} else if (autoCorrectWorkingTime && autoCorrectProjectTime) {
			message = "combinedRecording.autocorrect.message.BOTH";
		} else if (autoCorrectWorkingTime) {
			message = "combinedRecording.autocorrect.message.WORKING_TIME";
		} else if (autoCorrectProjectTime) {
			message = "combinedRecording.autocorrect.message.PROJECT_TIME";
		}

		// display dialog containing the auto-correction option or a warning that dates are on different day
		BootstrapModal.confirm({
			title: "recording.warning.header",
			// display appropriate for auto-correction or "different-date" warning
			message: message,
			buttons: [{
				text: "dialog.save",
				action: modal => {
					success();
					modal.close();
				},
				css: "btn-primary"
			}, {
				text: "dialog.autocorrect",
				visible: !autoCorrectNotAvailable,
				action: modal => {
					if (autoCorrectWorkingTime) {
						this.autoCorrectEndDateTime("workingTime");
					}
					if (autoCorrectProjectTime) {
						this.autoCorrectEndDateTime("projectTime")
					}
					success();
					modal.close();
				}
			}, {
				text: "dialog.cancel"
			}]
		});
	}

	/**
	 * Handles the switch recording action.
	 * Validates the stop recording process before switching the recording procedure.
	 *
	 * @param quickSwitchProcedure The recording procedure to switch to.
	 * @param workingTime The working time to switch to.
	 * @param projectTime The project time to switch to.
	 */
	private quickSwitchRecordingIfValid(quickSwitchProcedure: RecordingProcedure, workingTime: WorkingTime, projectTime: ProjectTime): void {
		// prepare stop is also used for a switch because stopping the record is required
		this.prepareStopRecording(quickSwitchProcedure, () => {
			const switchButton = this.config.panel.find("#combinedRecordingSwitchDropdown > a[data-toggle='dropdown']");
			this.runWithActionButtonLoading(switchButton, () =>
				this.startQuickSwitchRecording(quickSwitchProcedure, workingTime, projectTime).done(() => Notify.success(messages["recording.switched"])))
		});
	}

	/**
	 * Handles the switch recording action.
	 * Validates the stop recording process before switching the recording procedure.
	 *
	 * @param procedure The recording procedure to switch to.
	 * @param quickSwitchTaskId (optional) The task ID to switch to; only used for quick switch via task info dialog
	 */
	private shouldSwitchRecording(procedure: RecordingProcedure, quickSwitchTaskId: number = null): void {
		// prepare stop is also used for a switch because stopping the record is required
		this.prepareStopRecording(procedure, () => {
			const switchButton = this.config.panel.find("#combinedRecordingSwitchDropdown > a[data-toggle='dropdown']");
			this.runWithActionButtonLoading(switchButton, () => this.validateAndExecuteSwitch(procedure, params => {
				if (quickSwitchTaskId === null) {
					const resumingBreak = this.isBreakTimeWithPositionRunning() && isWorkingTimeIncluded(procedure);
					const lastWorkingTime = resumingBreak ? this.getLastWorkingTime() : null;
					const lastProjectTime = resumingBreak ? this.getLastProjectTime() : null;
					const resumeAction = resumingBreak
						? this.getResumeAction(lastWorkingTime, lastProjectTime, this.getRunningWorkingTime()) : null;
					const dialogProcedure = resumeAction === "both" ? RecordingProcedure.Both
						: resumingBreak ? RecordingProcedure.WorkingTime : procedure;
					this.switchResumeCombinedRecordingDialog.show(
						dialogProcedure,
						this.getEnd(),
						(workingTimeTypeId: number | null, taskId: number | null) =>
							this.switchCombinedRecording(procedure, params, workingTimeTypeId, taskId)
								.done(() => Notify.success(messages["recording.switched"])),
						resumingBreak ? lastWorkingTime?.workingTimeType : this.getSelectedWorkingTimeType(),
						"switch"
					);
					return $.Deferred<CombinedRecordingStatus>().reject().promise();
				} else {
					return this.switchCombinedRecording(procedure, params, null, quickSwitchTaskId)
						.done(() => Notify.success(messages["recording.switched"]));
				}
			}));
		});
	}

	/**
	 * Switches the combined recording procedure.
	 *
	 * @param procedure - The recording procedure for the switch action.
	 * @param params - The serialized parameters to be sent with the switch request.
	 * @param workingTimeTypeId - The ID of the working time type to switch to (or null if not applicable).
	 * @param taskId - The ID of the task to switch to (or null if not applicable).
	 */
	private switchCombinedRecording(
		procedure: RecordingProcedure,
		params: RequestParam[],
		workingTimeTypeId: number | null,
		taskId: number | null,
	): JQuery.jqXHR<CombinedRecordingStatus> {
		const form = this.config.panel.find("#combinedRecordingStatusForm");

		// add timestamp (for time manipulation detection)
		params.push({name: "timestamp", value: Date.now()});
		params.push({name: "procedure", value: procedure});

		if (workingTimeTypeId) {
			params.push({name: "newWorkingTimeTypeId", value: workingTimeTypeId});
		}

		if (taskId) {
			params.push({name: "newTaskId", value: taskId});
		}

		return api("timeline/switchCombinedRecording.json")
			.post(params)
			.done((status: CombinedRecordingStatus | null) => {
				if (this.reloadIfRecordingUnavailable(status)) {
					return;
				}

				// remove all feedback messages from the combined recording status form
				FormUtil.removeAllFeedback(form);

				// clear changed fields after update
				this.clearChangedFields(procedure);

				// update status of combined recording (also updates local variable this.currentStatus)
				this.updateStatus(status, procedure);

				// reset changed end fields/flag after success (changed start flags are already updated with updateStatus)
				this.resetChangedEndDateTimeState();

				// update timeline before status update that the current running recording is available in the status
				this.updateTimeline();

				if (isProjectTimeIncluded(procedure)) {
					this.taskDropdown.updateProjectTimeCount();
				}
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
				this.setActionButtonLoadingState(this.config.panel.find("#combinedRecordingSwitchDropdown > a"), false);
			});
	}

	/**
	 * Handles the start recording action.
	 * For the both procedure the project time start field is initialized by the working time start.
	 * The start of the records is validated before starting the recording.
	 *
	 * @param procedure The recording procedure to start.
	 */
	private shouldStartRecording(procedure: RecordingProcedure): void {
		// if both records are started in a state where nothing is running, the project time start is invisible
		// therefore the project time start is initialized by the working time start, changed the flag by and the changed working time start flag
		if (procedure === RecordingProcedure.Both) {
			this.setStart("projectTime", this.getStart("workingTime"));
			this.changedProjectTimeStart = this.changedWorkingTimeStart;
		}

		if (this.validateStart(procedure)) {
			const startButton = this.config.panel.find("#combinedRecordingStartBothDropdown > a[data-toggle='dropdown']");
			this.runWithActionButtonLoading(startButton, () => this.startRecording(procedure));
		}
	}

	/**
	 * Handles the stop recording action.
	 * Prepares the stop recording process, validates it, and then stops the recording.
	 *
	 * @param procedure The recording procedure to stop.
	 */
	private shouldStopRecording(procedure: RecordingProcedure): void {
		this.prepareStopRecording(procedure, () => {
			const stopButton = this.config.panel.find("#combinedRecordingStopDropdown > a[data-toggle='dropdown']");
			this.clearTimeout();
			this.runWithActionButtonLoading(stopButton, () => this.stopRecording(procedure));
		});
	}

	/**
	 * Handles pause action while preserving the existing "stop-like" validation path.
	 * In RECORD_WITH_POSITION mode pausing is modeled as a switch to a break working time type.
	 *
	 * @param procedure The recording procedure currently paused in legacy mode.
	 */
	private shouldPauseRecording(procedure: RecordingProcedure): void {
		this.prepareStopRecording(procedure, () => {
			const pauseButton = this.config.panel.find("#combinedRecordingPauseButton, #combinedRecordingPauseDropdown > a[data-toggle='dropdown']");
			this.runWithActionButtonLoading(pauseButton, () => this.isBreakTimeWithPositionApplicable(procedure)
				? this.pauseBySwitchToBreakType()
				: this.pauseRecording(procedure));
		})
	}

	/**
	 * Handles resume action while preserving the existing validation path.
	 * In RECORD_WITH_POSITION mode resuming is implemented by switching back from break to the last active type.
	 *
	 * @param procedure The recording procedure currently resumed in legacy mode.
	 */
	private shouldResumeRecording(procedure: RecordingProcedure): void {
		this.prepareStopRecording(procedure, () => {
			const resumeButton = this.config.panel.find("#combinedRecordingResumeButton, #combinedRecordingPauseDropdown > a[data-toggle='dropdown']");
			this.runWithActionButtonLoading(resumeButton, () => this.isBreakTimeWithPositionApplicable(procedure)
				? this.resumeBySwitchFromBreakType()
				: this.resumeRecording(procedure));
		});
	}


	/**
	 * Pauses recording by switching to a break time type.
	 * The method checks for available break time types and determines the appropriate switching procedure based on the current recording state.
	 * If there is only one break time type available, it directly switches to that type. If multiple break time types are available, it shows a dialog for the user to select the desired break time type.
	 */
	private pauseBySwitchToBreakType(): JQuery.jqXHR<CombinedRecordingStatus> | JQueryPromise<CombinedRecordingStatus> {
		// if there are no break time types available, pausing is not possible, so the action is aborted with an info dialog
		if (this.config.breakTimeTypes.length === 0) {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "recording.switchToBreakTime.noBreakTime",
			});
			return $.Deferred<CombinedRecordingStatus>().reject().promise();
		}

		// procedure is determined based on the current running state: if both are running, both are switched to break time, otherwise only the running one is switched
		const procedure = this.isProjectTimeRunning() ? RecordingProcedure.Both : RecordingProcedure.WorkingTime;
		return this.validateAndExecuteSwitch(procedure, params => {
			if (this.config.breakTimeTypes.length === 1) {
				return this.switchCombinedRecording(procedure, params, this.config.breakTimeTypes[0].id, null)
					.done(() => Notify.success(messages["recording.paused"]));
			} else {
				PauseCombinedRecordingDialog.show({
					breakTimeTypes: this.config.breakTimeTypes,
					endTime: this.getEnd(),
					pauseExecutor: (workingTimeTypeId: number) =>
						this.switchCombinedRecording(procedure, params, workingTimeTypeId, null)
							.done(() => Notify.success(messages["recording.paused"])),
					preselectWorkingTimeType: this.getLastWorkingTime()?.workingTimeType,
					onDialogClosed: () => this.activateUpdater()
				});

				// Dialog flow is asynchronous and continues after user selection.
				return $.Deferred<CombinedRecordingStatus>().reject().promise();
			}
		});
	}

	/**
	 * Resumes recording by determining the appropriate type of recording to switch to based on the current break state.
	 * Depending on the available data and user interaction requirements, it may directly resume the recording
	 * or display a dialog for the user to select the desired recording type.
	 *
	 * - If both working time and project time can be resumed, the method resumes both directly.
	 * - If only working time can be resumed, it validates and executes the required action to switch the recording.
	 * - If no specific resume target can be determined, it shows a dialog for user selection.
	 *
	 * @return {JQueryPromise<CombinedRecordingStatus>} A promise that resolves with the status of the combined recording after the resume operation is executed.
	 */
	private resumeBySwitchFromBreakType(): JQuery.jqXHR<CombinedRecordingStatus> | JQueryPromise<CombinedRecordingStatus> {
		const lastWorkingTime = this.getLastWorkingTime();
		const lastProjectTime = this.getLastProjectTime();

		// indicates how a running break will be resumed (directly, with both records, or via switch dialog).
		const resumeAction = this.getResumeAction(lastWorkingTime, lastProjectTime, this.getRunningWorkingTime());

		// if both can be resumed, they are resumed directly without showing the switch dialog
		if (resumeAction === "both") {
			return this.startQuickSwitchRecording(RecordingProcedure.Both, lastWorkingTime, lastProjectTime)
				.done(() => Notify.success(messages["recording.resumed"]));
		}

		// if only working time can be resumed, it is resumed directly without showing the switch dialog
		if (resumeAction === "workingTime") {
			return this.startQuickSwitchRecording(RecordingProcedure.Both, lastWorkingTime, null)
				.done(() => Notify.success(messages["recording.resumed"]));
		}

		// if no explicit resume target can be derived, the switch dialog is shown to select working time, project time or both
		return this.resumeBySwitchRecording();
	}

	/**
	 * Determines the appropriate action to resume work after a break, based on the provided parameters.
	 * The method evaluates the last recorded working time, project time, and the currently running working time
	 * To decide whether to resume only the working time, both working and project time, or to show a dialog for user selection, see TIMR-9579
	 *
	 * @param {WorkingTime} lastWorkingTime - The last recorded working time before the break.
	 * @param {ProjectTime} lastProjectTime - The last recorded project time before the break.
	 * @param {RunningWorkingTime} runningWorkingTime - The currently running working time associated with the break.
	 * @return {ResumeAction} The determined action for resuming work, which can be one of the following:
	 * - `"workingTime"`: Resume only the working time.
	 * - `"both"`: Resume both the working time and project time.
	 * - `"switchDialog"`: Switch to recording both working time and project time.
	 */
	private getResumeAction(lastWorkingTime: WorkingTime, lastProjectTime: ProjectTime, runningWorkingTime: RunningWorkingTime): ResumeAction {
		const lastWorkingTimeTypeId = lastWorkingTime?.workingTimeType?.id;
		const breakStart = runningWorkingTime?.start;
		const isBreakRunning = runningWorkingTime?.workingTimeType?.category === WorkingTimeTypeCategory.BreakTime;

		// if there is no break running or no break start time or the last working time type cannot be determined, there is no basis to determine a resume target, so the switch dialog is shown to select the desired resume target
		if (!isBreakRunning || !breakStart || !lastWorkingTimeTypeId) {
			return "switchDialog";
		}

		// when working time only is activated and last working time end matches with the break time starte it can be resumed seemingly without showing the switch dialog, see TIMR-11858
		if (!this.isProjectTimeEnabled() && this.isSameDateTime(lastWorkingTime?.end, breakStart)) {
			return "workingTime";
		}

		// if the last working time does not end with the break start, it cannot be resumed as is, so switch dialog is shown to select the desired resume target
		if (!this.isSameDateTime(lastWorkingTime?.end, breakStart)) {
			return "switchDialog";
		}

		// if the last working time type is not an attendance time, we can be sure that only the working time should be resumed, because project time cannot run without an attendance time
		const isAttendanceTime = lastWorkingTime?.workingTimeType?.category === WorkingTimeTypeCategory.AttendanceTime
		if (!isAttendanceTime) {
			return "workingTime";
		}

		// if the last project time ends with the break start and has a task assigned, we can be sure that both working time and project time should be resumed
		if (!!lastProjectTime?.task?.id
			&& this.isSameDateTime(lastProjectTime?.end, lastWorkingTime?.end)
			&& this.isSameDateTime(lastProjectTime?.end, breakStart)) {
			return "both";
		}

		return this.config.recordingMode === RecordingMode.COMBINED ? "workingTime" : "switchDialog";
	}

	/**
	 * Handles the resumption of recording by switching to a different recording procedure.
	 * The method validates the current state and initiates a dialog to allow the user to switch the recording context.
	 *
	 * The dialog is presented asynchronously and processes the user's selection for combined recording.
	 * If the recording procedure involves break time, additional checks may be performed based on the selected working time type.
	 *
	 * @return {JQueryPromise<CombinedRecordingStatus>} A promise that resolves with the recording status upon successful execution
	 *                                                 or rejects if the operation is terminated during dialog handling.
	 */
	private resumeBySwitchRecording(): JQueryPromise<CombinedRecordingStatus> {
		return this.validateAndExecuteSwitch(RecordingProcedure.WorkingTime, params => {
			this.switchResumeCombinedRecordingDialog.show(
				RecordingProcedure.WorkingTime,
				this.getEnd(),
				(workingTimeTypeId: number, taskId: number) => {
					// project time can only be resumed on a panel where it is enabled; a working-time-only panel resumes working time only
					const procedure = this.isProjectTimeEnabled() ? RecordingProcedure.Both : RecordingProcedure.WorkingTime;
					return this.switchCombinedRecording(procedure, params, workingTimeTypeId, taskId)
						.done(() => Notify.success(messages["recording.resumed"]));
				},
				this.getLastWorkingTime()?.workingTimeType,
				"resume"
			);
			// dialog flow continues asynchronously after user selection
			return $.Deferred<CombinedRecordingStatus>().reject().promise();
		});
	}

	/**
	 * Executes the given action while setting the loading state of the specified button.
	 * The button's loading state is set to true before executing the action and reset to false once the action is completed, regardless of success or failure.
	 * @param button The jQuery element representing the button for which the loading state should be managed.
	 * @param action A function that returns a jQuery promise representing the action to be executed while the button is in the loading state.
	 */
	private runWithActionButtonLoading(button: JQuery, action: () => JQuery.jqXHR<unknown> | JQueryPromise<unknown>): void {
		this.setActionButtonLoadingState(button, true);
		void action().always(() => this.setActionButtonLoadingState(button, false));
	}

	/**
	 * Handles the delete recording action.
	 * Shows a confirmation dialog if the recording was started more than 1 minute ago.
	 * If confirmed, it starts the process to delete the recording.
	 *
	 * @param procedure The recording procedure to delete.
	 */
	private shouldDeleteRecording(procedure: RecordingProcedure): void {
		// end time may be edited if the date and time of at least one of the records are modifiable. Therefore, it is required to validate that the action is allowed for the potentially edited end time.
		if (!this.isActionAllowedForEditedEndTime(procedure)) {
			return;
		}

		const performDeleteRecording = () => {
			const deleteButton = this.config.panel.find("#combinedRecordingDeleteDropdown > a[data-toggle='dropdown']");
			this.clearTimeout();
			this.runWithActionButtonLoading(deleteButton, () => this.deleteRecording(procedure));
		};

		// Only show the confirmation dialog if the recording was started more than 1 minutes ago
		if (this.showDeleteWarning(procedure)) {
			BootstrapModal.confirm({
				title: "combinedRecording.deleteDialog.title." + procedure.toString(),
				message: "combinedRecording.deleteDialog.message." + procedure.toString(),
				yesLabel: "combinedRecording.deleteDialog.button.delete." + procedure.toString(),
				yesAction: modal => {
					performDeleteRecording();
					modal.close();
				},
				noLabel: "dialog.cancel"
			});
		} else {
			performDeleteRecording();
		}
	}

	/**
	 * Shows delete warning if duration is above one hour
	 * @param procedure working time, project time or both
	 */
	private showDeleteWarning(procedure: RecordingProcedure): boolean {
		const isDurationOver1Minute = (runningEntry: RunningEntry) => calculateDurationSince(runningEntry.start, runningEntry.breakTime).total('minutes') >= 1;
		switch (procedure) {
			case RecordingProcedure.Both:
				return isDurationOver1Minute(this.getRunningWorkingTime()) || isDurationOver1Minute(this.getRunningProjectTime())
			case RecordingProcedure.WorkingTime:
				return isDurationOver1Minute(this.getRunningWorkingTime())
			case RecordingProcedure.ProjectTime:
				return isDurationOver1Minute(this.getRunningProjectTime())
		}
	}

	/**
	 * Initiates a quick switch recording for either working time or project time,
	 * based on the specified parameters and current recording state. This method
	 * updates the recording status, handles time adjustments, and sends data to
	 * the server.
	 *
	 * @param {RecordingProcedure} quickSwitchProcedure - The target procedure for the switch action e.g. if select item is working time -> WORKING_TIME, project time, or both
	 *													  according to google docs sheet in TIMR-9883
	 * @param {WorkingTime} workingTime - The working time data used for the quick switch procedure.
	 * @param {ProjectTime} projectTime - The project time data used for the quick switch procedure.
	 * @return {JQueryPromise<CombinedRecordingStatus>} A jQuery promise that resolves with the updated combined recording status or fails with an error response.
	 */
	private startQuickSwitchRecording(quickSwitchProcedure: RecordingProcedure, workingTime: WorkingTime, projectTime: ProjectTime): JQuery.jqXHR<CombinedRecordingStatus> {
		this.updateEndTimeForNonRunningEntries();

		const form = this.config.panel.find("#combinedRecordingStatusForm");

		// quick switch behaves the same as a switch via dialog (basically), so current state of the panel (WT, PT, or BOTH) is passed here as a procedure
		// if nothing is running, quickSwitchProcedure is used which specifies the selected item item/subitem -> e.g selected Project Time inside Attendance Time leads to BOTH
		const procedure = this.getRecordingProcedure() ?? quickSwitchProcedure;

		const end = this.getEnd();
		const now = Temporal.Now.zonedDateTimeISO();
		const params: RequestParam[] = form.serializeArray();
		params.push({name: "procedure", value: procedure});
		params.push({name: "changedNewStart", value: this.changedEnd});
		params.push({name: "localTimeZone", value: now.offset});
		params.push({name: "timestamp", value: now.epochMilliseconds.toString()});
		params.push({name: "projectTime.end", value: getISOZonedDateTime(end)});
		params.push({name: "workingTime.end", value: getISOZonedDateTime(end)});
		params.push({name: "projectTime.changed", value: this.isDateTimeChanged("projectTime", procedure)});
		params.push({name: "workingTime.changed", value: this.isDateTimeChanged("workingTime", procedure)});

		// add selected working time id if currently working time is running and included in quick switch procedure e.g. -> if working time and project time selected item is a different working time
		// or if procedure is project time e.g. -> if project time is running, selected working time is to be started
		if (isWorkingTimeIncluded(procedure) && isWorkingTimeIncluded(quickSwitchProcedure) ||
			procedure === RecordingProcedure.ProjectTime) {
			params.push({name: "sourceWorkingTimeId", value: workingTime?.id});
		}

		// add selected project time id if included in quick switch procedure (subitem selected)
		if (isProjectTimeIncluded(quickSwitchProcedure)) {
			params.push({name: "sourceProjectTimeId", value: projectTime?.id});
		}

		return api("timeline/quickSwitchCombinedRecording.json")
			.post(params)
			.done((status: CombinedRecordingStatus | null) => {
				if (this.reloadIfRecordingUnavailable(status)) {
					return;
				}

				this.clearChangedFields(procedure);
				FormUtil.removeAllFeedback(form);

				this.descriptionTypeaheadNeedReset(this.config.activeRecordingProcedure);

				// update status of combined recording (also updates local variable this.currentStatus)
				this.updateStatus(status, this.config.activeRecordingProcedure);

				this.updateTimeline(true);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
				this.setActionButtonLoadingState(this.config.panel.find("#combinedRecordingSwitchDropdown > a"), false);
			});
	}

	/**
	 * Validates and executes the switch recording action. This method first updates the end time for any non-running entries,
	 * then prepares the parameters for the switch action, including validation of changed fields and time zones.
	 * It sends a validation request to the server, and if successful, executes the provided action to perform the switch.
	 * @param procedure The (current) recording procedure for which the switch action is being performed (e.g., working time, project time, or both).
	 * @param action A callback function that takes the prepared parameters and returns a jQuery promise for the switch action to be executed if validation is successful.
	 * @private
	 */
	private validateAndExecuteSwitch(
		procedure: RecordingProcedure,
		action: (params: RequestParam[]) => JQuery.jqXHR<CombinedRecordingStatus> | JQueryPromise<CombinedRecordingStatus>
	): JQueryPromise<CombinedRecordingStatus> {
		this.updateEndTimeForNonRunningEntries();

		const form = this.config.panel.find("#combinedRecordingStatusForm");
		const end = this.getEnd();

		// determine if end date time was changed; this is used to determine changed state for the newly started (switched to) record, because if end date time was changed, it is considered as changed new start for the new record
		const hasChangedEnd = this.changedFields.has("combinedRecording.endDate")
			|| this.changedFields.has("combinedRecording.endTime")
			|| this.changedFields.has("combinedRecording.endTimeZone");

		const params: RequestParam[] = form.serializeArray();
		params.push(
			{name: "procedure", value: procedure},
			{name: "workingTime.end", value: getISOZonedDateTime(end)},
			{name: "projectTime.end", value: getISOZonedDateTime(end)},
			{name: "workingTime.changed", value: this.isDateTimeChanged("workingTime", procedure)},
			{name: "projectTime.changed", value: this.isDateTimeChanged("projectTime", procedure)},
			{name: "changedNewStart", value: hasChangedEnd},
			{name: "localTimeZone", value: getCurrentISOOffset()},
		);

		const deferred = $.Deferred<CombinedRecordingStatus>();
		this.validateCombinedRecording(params)
			.done(() => {
				action(params)
					.done((status: CombinedRecordingStatus) => deferred.resolve(status))
					.fail((response: ErrorResponse) => deferred.reject(response));
			})
			.fail((response: ErrorResponse) => deferred.reject(response));

		return deferred.promise();
	}

	/**
	 * Validates the switch recording action by sending a request to the server.
	 * This method checks if the parameters are valid (e.g. mandatory task note) and returns a promise.
	 * @param params - An array of serialized parameters to be sent with the API request.
	 */
	private validateCombinedRecording(params: RequestParam[]): JQuery.jqXHR<void> {
		return api("timeline/validateCombinedRecording.json")
			.get(params)
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(this.config.panel.find("#combinedRecordingStatusForm"), response.responseJSON.fieldErrors);
				}
				this.setActionButtonLoadingState(this.config.panel.find("#combinedRecordingSwitchDropdown > a"), false);
			});
	}

	/**
	 * Triggers endpoint to start a running recording
	 * @param procedure working time, project time or both
	 */
	private startRecording(procedure: RecordingProcedure): JQuery.jqXHR<CombinedRecordingStatus> {
		if (!this.isWorkingTimeRunning()) {
			this.setStartToNowIfNotChanged("workingTime");
		}
		if (!this.isProjectTimeRunning()) {
			this.setStartToNowIfNotChanged("projectTime");
		}

		const form = this.config.panel.find("#combinedRecordingStatusForm");
		const params: RequestParam[] = form.serializeArray();
		params.push({name: "procedure", value: procedure.toString()});
		const now = Temporal.Now.zonedDateTimeISO();
		params.push({name: "localTimeZone", value: now.offset});
		params.push({name: "timestamp", value: now.epochMilliseconds});
		params.push({name: "workingTime.changed", value: this.changedWorkingTimeStart});
		params.push({name: "projectTime.changed", value: this.changedProjectTimeStart});

		return api("timeline/startCombinedRecording.json")
			.post(params)
			.done((data: CombinedRecordingStatus | null) => {
				if (this.reloadIfRecordingUnavailable(data)) {
					return;
				}

				FormUtil.removeAllFeedback(form);
				this.clearChangedFields(procedure);

				// update status of combined recording (also updates local variable this.currentStatus)
				this.updateStatus(data, procedure);

				// update timeline that the correct state of current running recording is available
				this.updateTimeline();

				Notify.success(messages["recording.started"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			});
	}

	/**
	 * Triggers endpoint to stop the running recording
	 * @param procedure working time, project time or both
	 */
	private stopRecording(procedure: RecordingProcedure): JQuery.jqXHR<CombinedRecordingStatus> {
		this.updateEndTimeForNonRunningEntries();

		const end = this.getEnd();
		const now = Temporal.Now.zonedDateTimeISO();
		const form = this.config.panel.find("#combinedRecordingStatusForm");
		const params = form.serialize() + "&" + $.param({
			procedure: procedure.toString(),
			localTimeZone: now.offset,
			timestamp: now.epochMilliseconds,
			"projectTime.end": getISOZonedDateTime(end),
			"workingTime.end": getISOZonedDateTime(end),
			"workingTime.changed": this.isDateTimeChanged("workingTime", procedure).toString(),
			"projectTime.changed": this.isDateTimeChanged("projectTime", procedure).toString()
		});

		return api("timeline/stopCombinedRecording.json")
			.post(params)
			.done((status: CombinedRecordingStatus | null) => {
				if (this.reloadIfRecordingUnavailable(status)) {
					return;
				}

				FormUtil.removeAllFeedback(form);
				this.clearChangedFields(procedure);

				// update status of combined recording (also updates local variable this.currentStatus)
				this.updateStatus(status, procedure);

				// update timeline that the correct state of current running recording is available
				this.updateTimeline();

				// reset changed end fields/flag after success (changed start flags are already updated with updateStatus)
				this.resetChangedEndDateTimeState();

				if (isProjectTimeIncluded(procedure)) {
					this.taskDropdown.updateProjectTimeCount();
				}
				Notify.success(messages["recording.stopped"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			});
	}

	/**
	 * Triggers endpoint call to delete a running recording
	 * @param procedure working time, project time or both
	 */
	private deleteRecording(procedure: RecordingProcedure): JQuery.jqXHR<CombinedRecordingStatus> {
		const form = this.config.panel.find("#combinedRecordingStatusForm");
		return api("timeline/deleteCombinedRecording.json")
			.post({
				procedure: procedure.toString(),
				localTimeZone: getCurrentISOOffset(),
				workingTimeRecordId: this.config.panel.find("[name='workingTime.recordId']").val() as string,
				projectTimeRecordId: this.config.panel.find("[name='projectTime.recordId']").val() as string
			})
			.done((status: CombinedRecordingStatus | null) => {
				if (this.reloadIfRecordingUnavailable(status)) {
					return;
				}

				FormUtil.removeAllFeedback(form);
				this.clearChangedFields(procedure);

				// update status of combined recording (also updates local variable this.currentStatus)
				this.updateStatus(status, procedure);

				// update timeline that the correct state of current running recording is available
				this.updateTimeline();

				// reset changed end fields/flag after success (changed start flags are already updated with updateStatus)
				this.resetChangedEndDateTimeState();

				Notify.success(messages["recording.deleted"]);
			})
			.fail((response: ErrorResponse) => {
				// E0200 = The recording has been changed elsewhere in the meantime. The data will be reloaded.
				// DEVOTE: duplicated snipped from wt/pt/recordingStatusPanel (TIMR-8418)
				if (response.responseJSON?.fieldErrors?.find(fe => fe.code === "E0200")) {
					this.fetchRecordingStatus();
				}
			});
	}

	private pauseRecording(procedure: RecordingProcedure): JQuery.jqXHR<CombinedRecordingStatus> {
		const now = Temporal.Now.zonedDateTimeISO();
		return api("timeline/pauseRunningCombinedRecording.json")
			.post({
				procedure: procedure,
				breakTimeAt: getISOZonedDateTime(now),
				localTimeZone: now.offset,
				workingTimeId: this.config.panel.find("[name='workingTime.recordId']").val() as string,
				projectTimeId: this.config.panel.find("[name='projectTime.recordId']").val() as string,
				timestamp: now.epochMilliseconds
			})
			.done((status: CombinedRecordingStatus | null) => {
				if (this.reloadIfRecordingUnavailable(status)) {
					return;
				}

				// update status of combined recording (also updates local variable this.currentStatus)
				this.updateStatus(status, procedure);

				// update timeline that the correct state of current running recording is available
				this.updateTimeline();

				Notify.success(messages["recording.paused"]);
			})
			.fail((response: ErrorResponse) => {
				// E0200 = The recording has been changed elsewhere in the meantime. The data will be reloaded.
				// DEVOTE: duplicated snipped from wt/pt/recordingStatusPanel (TIMR-8418)
				if (response.responseJSON?.fieldErrors?.find(fe => fe.code === "E0200")) {
					this.fetchRecordingStatus();
				}
			});
	}

	private resumeRecording(procedure: RecordingProcedure): JQuery.jqXHR<CombinedRecordingStatus> {
		const now = Temporal.Now.zonedDateTimeISO();
		return api("timeline/resumeRunningCombinedRecording.json")
			.post({
				procedure: procedure,
				breakTimeAt: getISOZonedDateTime(now),
				localTimeZone: now.offset,
				workingTimeId: this.config.panel.find("[name='workingTime.recordId']").val() as string,
				projectTimeId: this.config.panel.find("[name='projectTime.recordId']").val() as string,
				timestamp: now.epochMilliseconds
			})
			.done((status: CombinedRecordingStatus | null) => {
				if (this.reloadIfRecordingUnavailable(status)) {
					return;
				}

				// update status of combined recording (also updates local variable this.currentStatus)
				this.updateStatus(status, procedure);

				// update timeline that the correct state of current running recording is available
				this.updateTimeline();

				Notify.success(messages["recording.resumed"]);
			})
			.fail((response: ErrorResponse) => {
				// E0200 = The recording has been changed elsewhere in the meantime. The data will be reloaded.
				// DEVOTE: duplicated snipped from wt/pt/recordingStatusPanel (TIMR-8418)
				if (response.responseJSON?.fieldErrors?.find(fe => fe.code === "E0200")) {
					this.fetchRecordingStatus();
				}
			});
	}

	/**
	 * Returns parsed date object from start date/time input field for given type
	 * @param type working time or project time
	 */
	private getStart(type: RecordingType): Temporal.ZonedDateTime | null {
		const dateString = this.config.panel.find(`[name="${type}.startDate"]`).val() as string;
		const timeString = this.config.panel.find(`[name="${type}.startTime"]`).val() as string;
		const timeZoneString = this.config.panel.find(`[name="${type}.startTimeZone"]`).val() as string;
		return parseDateTimeZone(dateString, timeString, timeZoneString);
	}

	/**
	 * Returns parsed date object from end date/time input field for both types (end time is for working time and project time)
	 */
	private getEnd(): Temporal.ZonedDateTime | null {
		const dateString = this.config.panel.find("[name='combinedRecording.endDate']").val() as string;
		const timeString = this.config.panel.find("[name='combinedRecording.endTime']").val() as string;
		const timeZoneString = this.config.panel.find("[name='combinedRecording.endTimeZone']").val() as string;
		return parseDateTimeZone(dateString, timeString, timeZoneString);
	}

	/**
	 * Clears basic fields like recordId, version, break time, changed and custom fields for given type
	 * Other non-generic fields such as working time type, task, billable, description, etc. must be treated separately
	 *
	 * PLEASE NOTE: Description field is not cleared, as there are some differences regarding the required descriptions. This must be handled separately.
	 * @param type working time or project time
	 */
	private clearBasicFields(type: RecordingType): void {
		this.config.panel.find(`[name="${type}.recordId"]`).val("");
		this.config.panel.find(`[name="${type}.version"]`).val("-1");
		this.config.panel.find(`[name="${type}.description"]`).val("");
		this.config.panel.find(`[name="${type}.breakTime"]`).val("");
		this.config.panel.find(`[name="${type}.changed"]`).val(String(false));

		this.clearCustomFields(type);
	}

	/**
	 * Clears custom fields for given type
	 * @param type working time or project time
	 */
	private clearCustomFields(type: RecordingType): void {
		// reset inputs for text and number -> empty string (CustomFieldsForRecordType.String and Number)
		this.config.panel.find(`
				input[name^="${type}.customField"][data-custom-field-type="${CustomFieldsForRecordType.String}"], 
				input[name^="${type}.customField"][data-custom-field-type="${CustomFieldsForRecordType.Number}"]`
		).val("");

		// reset inputs for checkboxes -> "false" (CustomFieldsForRecordType.Checkbox)
		this.config.panel.find(`input[name^="${type}.customField"][data-custom-field-type="${CustomFieldsForRecordType.Checkbox}"]`).val("false");
		this.config.panel.find(`[id^="${type}.customField"]:checkbox`).prop("checked", false);

		// reset inputs for for comboboxes (CustomFieldsForRecordType.Options)
		if (type === "workingTime") {
			this.workingTimeCustomFieldComboboxes.forEach((combobox: Combobox<CustomFieldOption>): void => {
				combobox.setEmptyMessage("");
				combobox.clearSelection(true);
			});
		}
		if (type === "projectTime") {
			this.projectTimeCustomFieldComboboxes.forEach((combobox: Combobox<CustomFieldOption>): void => {
				combobox.setEmptyMessage("");
				combobox.clearSelection(true);
			});
		}
	}

	/**
	 * Update fields recordId, version, break time, changed and description including the add/clear button with running recording for given type
	 * @param type working time or project time
	 * @param runningEntry running working time or project time
	 */
	private updateFieldsWithRunningRecording(type: RecordingType, runningEntry: RunningEntry): void {
		this.config.panel.find(`[name="${type}.recordId"]`).val(runningEntry.recordId);
		this.config.panel.find(`[name="${type}.version"]`).val(runningEntry.version);
		this.config.panel.find(`[name="${type}.breakTime"]`).val(runningEntry.breakTime);
		this.config.panel.find(`[name="${type}.changed"]`).val(String(runningEntry.changed));
	}

	/**
	 * Updates the contents of the description field, the visibility, and the status of the toggle button for the specified type (working time or project time).
	 * This method checks whether the description should be visible based on the availability of the <code>type</code>.
	 * <code>isDescriptionRequired</code> flag (currently only relevant for project time) and <code>description</code>.
	 *
	 * If the description text should not be updated, but only the visibility and status of the toggle button <code>description</code> must be provided
	 * If the description field should be reset, an empty string "" or <code>null</code> must be passed as <code>description</code>.
	 *
	 * @param type the type of recording, either "workingTime" or "projectTime"
	 * 						- if the description is to be resetted -> pass empty string "" or null
	 * 						- if only the visibility and state of toggle description button should not be updated -> undefined
	 * @param isDescriptionRequired boolean indicating if the description is required (currently only relevant for project time)
	 * @param newDescription description of the record as string to update the description field value text, following special cases:
	 * 						- if the description is to be resetted -> pass empty string "" or null
	 * 						- if only the visibility and state of toggle description button should not be updated -> undefined
	 */
	private updateDescriptionField(type: "workingTime" | "projectTime", isDescriptionRequired: boolean, newDescription?: string): void {
		const descriptionField = this.config.panel.find(`[name="${type}.description"]`);

		// check if the description field is required and not blank (based on newDescription if provided or current value of the field)
		const isDescriptionNotBlank: boolean = newDescription !== undefined ? stringNotBlank(newDescription) : stringNotBlank(descriptionField.val() as string);

		// determine if the description should be shown
		const showDescription: boolean = isDescriptionRequired || isDescriptionNotBlank;

		Input.removeFeedback(descriptionField);

		// update the description field value if new description is provided
		if (newDescription !== undefined) {
			descriptionField.val(newDescription);
		}

		// update visibility of the description container
		descriptionField
			.closest("[data-description-container]")
			.toggle(showDescription);

		if (type === "projectTime") {
			descriptionField.attr("placeholder", isDescriptionRequired ? messages["combinedRecording.description.placeholder.required.PROJECT_TIME"] : null);
		}

		// update description button based on whether the description is required or not and if description is available
		let procedure: DescriptionToggleMode;
		if (isDescriptionRequired) {
			procedure = "required";
		} else {
			procedure = showDescription ? "clear" : "add";
		}
		this.updateDescriptionButton(type, procedure, isDescriptionNotBlank);
		this.onDescriptionFieldChange(type);
	}

	private updateCustomFields(type: RecordingType, runningEntry: RunningEntry) {
		const comboboxes = this.getCustomFieldComboboxes(type);
		const customFieldDefinitions = this.getCustomFieldDefinitions(type);

		if (this.config.panel.find(".form-group-custom-field").length !== 0) {
			// initialize the index for the combobox array to update the values of the combobox correctly
			let index = 0;
			for (const definition of customFieldDefinitions) {
				const value: string = runningEntry[`customField${definition.fieldNumber}`] ?? "";
				if (definition.fieldType === CustomFieldsForRecordType.Checkbox) {
					const checked = "true" === value;
					this.config.panel.find(`[id='${type}.customField${definition.fieldNumber}CheckboxStatusPanel']`).prop("checked", checked);
					this.config.panel.find(`[name="${type}.customField${definition.fieldNumber}"]`).val(checked.toString());
				} else if (definition.fieldType === CustomFieldsForRecordType.Number) {
					this.config.panel.find(`[name="${type}.customField${definition.fieldNumber}Number"]`).val(formatCustomField(value, definition.fieldType));
				} else if (definition.fieldType === CustomFieldsForRecordType.Options && definition.fieldName) {
					this.updateCustomFieldComboboxes(comboboxes, definition, index, value);
					index++; // increase the index only for custom fields of type combobox
				} else {
					this.config.panel.find(`[name="${type}.customField${definition.fieldNumber}"]`).val(formatCustomField(value, definition.fieldType));
				}
			}
		}
	}

	private updateCustomFieldComboboxes(customFieldComboboxes: Combobox<CustomFieldOption>[], definition: CustomFieldDefinition, index: number, value: string): void {
		const items: CustomFieldOption[] = (definition.optionsList ?? [])
			.filter(item => item !== "")
			.map(item => ({id: item}));

		// update items in combobox array
		customFieldComboboxes[index].updateItems(items);

		if (value === "") {
			if (definition.mandatory) {
				customFieldComboboxes[index].setEmptyMessage(messages["recording.customFieldCombobox.empty"]);
				customFieldComboboxes[index].clearSelection(true);
			} else {
				customFieldComboboxes[index].setEmptyMessage("");
				customFieldComboboxes[index].clearSelection();
			}
		} else {
			customFieldComboboxes[index].setSelectedItem({id: value}, false, true, false);
		}
	}


	/**
	 * Updates the appearance of the panel based on the current recording state.
	 * Adjusts the background color of the project time container if the active recording procedure is set to "Both".
	 * Updates the panel's border color depending on the active recording procedure.
	 * Following states are possible:
	 * <ul>
	 * <li>BOTH:
	 * 	<ul>
	 * 		<li>If working time is running -> border color based on working time type category</li>
	 * 		<li>If project time is running -> background color (project time container) based on billable or not billable</li>
	 * 	</ul>
	 * </li>
	 * <li>WT/PT only:
	 * 	<ul>
	 * 		<li>If working time is running -> border color based on working time type category</li>
	 * 		<li>If project time is running -> border color based on billable or not billable</li>
	 * 	</ul>
	 * </li>
	 * </ul>
	 * @return {void} No return value.
	 */
	private updatePanelAppearanceForRecordingState(): void {
		// in case of active recording procedure BOTH, project time container has a background color based on billable or not billable which must be updated here
		if (this.config.activeRecordingProcedure === RecordingProcedure.Both) {
			this.updateProjectTimeContainerColor();
		}

		// always update the panel border color based on running working time (BOTH/WT only) or project time (PT only)
		this.updatePanelBorderColor();
	}

	/**
	 * Update background color of project time section, based on project time status
	 * <ul>
	 * <li>If not running 		-> grey</li>
	 * <li>If running:
	 * <ul>
	 * 		<li>billable 		-> green</li>
	 * 		<li>not billable 	-> orange</li>
	 * </li>
	 * </ul>
	 */
	private updateProjectTimeContainerColor(): void {
		const container = this.config.panel.find("#projectTimeContainer");

		if (this.getRunningProjectTime()) {
			const billable = this.getBillable();
			container
				.toggleClass("billable", billable)
				.toggleClass("not-billable", !billable);
		} else {
			container.removeClass("not-billable billable");
		}
	}

	/**
	 * Update border color of the panel, based on running working time
	 * If not running 	-> grey
	 * If running 		-> color based on working time type category
	 */
	private updatePanelBorderColor(): void {
		const panelBody = this.config.panel.find(".panel-body");
		const icon = this.config.panel.find(".recording-status-indicator");

		if (this.isWorkingTimeEnabled()) {
			Object.keys(WorkingTimeTypeCategory)
				.map(k => WorkingTimeTypeCategory[k as keyof typeof WorkingTimeTypeCategory] as string)
				.forEach(c => {
					panelBody.removeClass("border-" + c);
					icon.removeClass("icon-" + c);
				});

			const workingTimeType = this.getSelectedWorkingTimeType();
			if (this.isWorkingTimeRunning() && workingTimeType) {
				panelBody.addClass("border-" + workingTimeType.category);
				icon.addClass("icon-" + workingTimeType.category);
			}
		} else if (this.isProjectTimeRunning()) {
			const billable = this.getBillable();

			icon.toggleClass("icon-BILLABLE", billable);
			icon.toggleClass("icon-NOT_BILLABLE", !billable);

			panelBody.toggleClass("border-BILLABLE", billable);
			panelBody.toggleClass("border-NOT_BILLABLE", !billable);
		} else {
			panelBody.removeClass("border-BILLABLE").removeClass("border-NOT_BILLABLE");
			icon.removeClass("icon-BILLABLE").removeClass("icon-NOT_BILLABLE");
		}
	}

	/**
	 * Updates the fields related to project time.
	 */
	private updateProjectTimeFields(): void {
		const runningProjectTime = this.getRunningProjectTime();
		const lastProjectTime = this.getLastProjectTime();
		const currentTaskId = this.taskDropdown.getSelectedId();

		if (runningProjectTime !== null) {
			this.updateFieldsForRunningProjectTime(runningProjectTime);
		} else {
			this.updateFieldsForNonRunningProjectTime(lastProjectTime);
		}

		this.onDescriptionFieldChange("projectTime");

		if (currentTaskId !== this.taskDropdown.getSelectedId()) {
			this.onTaskChanged();
		}
	}

	/**
	 * Handles the running project time by updating the relevant fields and UI elements during a status update (fields).
	 * This method checks if the record ID or version has changed, updates the fields accordingly,
	 *
	 * @param runningProjectTime The current running project time entry.
	 */
	private updateFieldsForRunningProjectTime(runningProjectTime: RunningProjectTime): void {
		// record is considered as changed if either the record ID or the version has changed (background)
		const isRecordingChanged = this.config.panel.find("[name='projectTime.recordId']").val() !== runningProjectTime.recordId ||
			this.parseInt(this.config.panel.find("[name='projectTime.version']")) !== runningProjectTime.version;

		// Update fields if the record ID or version has changed
		if (isRecordingChanged) {
			this.updateFieldsWithRunningRecording("projectTime", runningProjectTime);
			this.updateCustomFields("projectTime", runningProjectTime);
			this.setBillable(runningProjectTime.billable);
			this.onTaskChanged();

			// update description and button to add/clear note
			this.updateDescriptionField("projectTime", runningProjectTime.task.descriptionRequired, runningProjectTime?.description);
		}

		// Update the running start and duration
		this.setStartIfNotChanged("projectTime", runningProjectTime, isRecordingChanged);
		this.updateProjectTimeDuration();

		// Update the project time info panel (contains task budget info)
		this.projectTimeInfoPanel.update(runningProjectTime.task.id, runningProjectTime.task.description, runningProjectTime, null, true);

		// Set the selected task if it hasn't been modified and isn't focused
		if (!this.changedFields.has("projectTime.task") && this.notFocused("projectTime.taskQuery")) {
			this.taskDropdown.setSelectedTask(runningProjectTime.task);
		}

		// when project time is running, the start fields (and break time) must be enabled if project time date/time change is allowed
		this.setStartDateTimeFieldsState("projectTime", isProjectTimeDateTimeChangeAllowed());
	}

	/**
	 * Handles the scenario when there is no running working time during a status update.
	 * It hides the break time field, clears fields if necessary, sets the start time to now,
	 * updates the duration, fills the project time type select, and updates the working time info panel.
	 *
	 * @param lastProjectTime The last project time entry.
	 */
	private updateFieldsForNonRunningProjectTime(lastProjectTime: ProjectTime): void {
		// Hide break time field and enable input
		this.config.panel.find("[name='projectTime.breakTime']").closest(".form-group").hide();
		Input.enable(this.config.panel.find("[name='projectTime.breakTime']"));

		// Clear fields if the version is not -1
		if (this.parseInt(this.config.panel.find("[name='projectTime.version']")) !== -1) {
			this.clearBasicFields("projectTime");
			this.updateDescriptionField("projectTime", this.taskDropdown.getSelectedData()?.descriptionRequired, "");
		}

		// Set the start time to now and update the duration
		this.setStartToNowIfNotChanged("projectTime");
		this.updateProjectTimeDuration();

		// Set the selected task if it hasn't changed and isn't focused
		if (!this.changedFields.has("projectTime.task") && this.notFocused("projectTime.taskQuery")) {
			this.taskDropdown.setSelectedTask(lastProjectTime?.task ?? null);
		}

		// Set the billable status if the last project time task exists and hasn't changed
		if (lastProjectTime?.task && !this.changedFields.has("projectTime.billable")) {
			this.setBillable(lastProjectTime.task.billable);
		}

		// Update the project time info panel with the selected task data
		const selectedTaskData = this.taskDropdown.getSelectedData();
		if (selectedTaskData) {
			this.projectTimeInfoPanel.update(selectedTaskData.id, selectedTaskData.description);
		}
	}

	/**
	 * Updates the fields related to working time.
	 */
	private updateWorkingTimeFields(): void {
		const runningWorkingTime = this.getRunningWorkingTime();
		const lastWorkingTime = this.getLastWorkingTime();
		const currentWorkingTimeType = this.getSelectedWorkingTimeType();

		// Handle fields for running working time if it exists, otherwise handle no running working time
		if (runningWorkingTime !== null) {
			this.updateFieldsForRunningWorkingTime(runningWorkingTime);
		} else {
			this.updateFieldsForNonRunningWorkingTime(lastWorkingTime);
		}

		// Trigger the description field change event for working time
		this.onDescriptionFieldChange("workingTime");

		// Check if the working time type has changed and trigger the change event if necessary
		if (currentWorkingTimeType !== this.getSelectedWorkingTimeType()) {
			this.onWorkingTimeTypeChanged();
		}
	}

	/**
	 * The working time types that may be selected while a working time recording is running.
	 *
	 * A recording that collects proof documents keeps its working time type for as long as it runs: attachments the
	 * user already uploaded would be lost when it is changed. And no running recording may be moved into such a type,
	 * because the whole recorded span would retroactively turn into a working time request when it is stopped - so
	 * those types are not offered at all. Switching (stop + start) stays unrestricted.
	 */
	private getSelectableWorkingTimeTypesForRunningRecording(runningWorkingTime: RunningWorkingTime): WorkingTimeType[] {
		const runningWorkingTimeType = runningWorkingTime.workingTimeType;
		if (isRunningWorkingTimeAttachmentEligible(runningWorkingTimeType)) {
			return [runningWorkingTimeType];
		}
		const workingTimeTypes = runningWorkingTimeType?.category === WorkingTimeTypeCategory.BreakTime ? this.config.breakTimeTypes : this.config.workingTimeTypesForRecording;
		return workingTimeTypes.filter((workingTimeType) => isRunningWorkingTimeTypeChangeAllowed(runningWorkingTimeType, workingTimeType));
	}

	/**
	 * Handles the running working time by updating the relevant fields and UI elements during a status update (fields).
	 * This method checks if the record ID or version has changed, updates the fields accordingly,
	 *
	 * @param runningWorkingTime The current running working time entry.
	 */
	private updateFieldsForRunningWorkingTime(runningWorkingTime: RunningWorkingTime): void {
		// record is considered as changed if either the record ID or the version has changed (background)
		const isRecordingChanged = this.config.panel.find("[name='workingTime.recordId']").val() !== runningWorkingTime.recordId ||
			this.parseInt(this.config.panel.find("[name='workingTime.version']")) !== runningWorkingTime.version;

		// Update fields if the record ID or version has changed
		if (isRecordingChanged) {
			this.updateFieldsWithRunningRecording("workingTime", runningWorkingTime);
			this.updateCustomFields("workingTime", runningWorkingTime);
			this.updateDescriptionField("workingTime", false, runningWorkingTime?.description);
		}

		// Update the running start and duration
		this.setStartIfNotChanged("workingTime", runningWorkingTime, isRecordingChanged);
		this.updateWorkingTimeDuration();

		// Update the working time info panel
		this.workingTimeInfoPanel.update(runningWorkingTime.workingTimeType);

		// Fill the working time type select
		DOMUtil.fillWorkingTimeTypeSelect(
			this.config.panel.find("[name='workingTime.workingTimeType.id']"),
			this.getSelectableWorkingTimeTypesForRunningRecording(runningWorkingTime),
			false
		);

		// Set the selected working time type if it hasn't been modified
		if (!this.changedFields.has("workingTime.workingTimeType.id")) {
			this.config.panel.find("[name='workingTime.workingTimeType.id']").val(runningWorkingTime.workingTimeType.id);
		}

		// when working time is running, the start fields (and break time) must be enabled if working time date/time change is allowed
		this.setStartDateTimeFieldsState("workingTime", isWorkingTimeDateTimeChangeAllowed());
	}

	/**
	 * Handles the scenario when there is no running working time during a status update.
	 * It hides the break time field, clears fields if necessary, sets the start time to now,
	 * updates the duration, fills the working time type select, and updates the working time info panel.
	 *
	 * @param lastWorkingTime The last working time entry or null if no entries exists.
	 */
	private updateFieldsForNonRunningWorkingTime(lastWorkingTime: WorkingTime | null): void {
		// Hide break time field and enable input
		this.config.panel.find("[name='workingTime.breakTime']").closest(".form-group").hide();
		Input.enable(this.config.panel.find("[name='workingTime.breakTime']"));

		// Clear fields if the version is not -1
		if (this.parseInt(this.config.panel.find("[name='workingTime.version']")) !== -1) {
			this.clearBasicFields("workingTime");
			this.updateDescriptionField("workingTime", false, "");
		}

		// Set the start time to now and update the duration
		this.setStartToNowIfNotChanged("workingTime");
		this.updateWorkingTimeDuration();

		// Fill the working time type select
		DOMUtil.fillWorkingTimeTypeSelect(this.config.panel.find("[name='workingTime.workingTimeType.id']"), this.config.workingTimeTypesForRecording, false);

		if (this.config.workingTimeTypesForRecording.some(type => type.id === lastWorkingTime?.workingTimeType.id) && !this.changedFields.has("workingTime.workingTimeType.id")) {
			this.config.panel.find("[name='workingTime.workingTimeType.id']").val(lastWorkingTime?.workingTimeType.id);
			this.workingTimeInfoPanel.update(lastWorkingTime?.workingTimeType);
		} else {
			this.workingTimeInfoPanel.update(this.getSelectedWorkingTimeType());
		}

		// if active recording procedure is Both, the start field is used for both the work and project start fields and break time (after starting, it is applied to the project time start)
		// therefore, date/time must be enabled for both work and project time when working time is not running
		this.setStartDateTimeFieldsState("workingTime",
			!(this.isWorkingTimeEnabled() && !isWorkingTimeDateTimeChangeAllowed() ||
				this.isProjectTimeEnabled() && !isProjectTimeDateTimeChangeAllowed()));
	}

	/**
	 * DEVNOTE: This is for a correct height of the description field because this field includes an autoExpand logic
	 * 			(not sure what it does exactly, this is copied from existing recordingStatusPanel, see TIMR-8418)
	 */
	private onDescriptionFieldChange(type: RecordingType): void {
		this.config.panel.find(`[name='${type}.description']`).change();
	}

	/**
	 * Updates the start fields for a running entry two some scenarios.
	 * If the entry is a new version or the start fields have not been changed or focused,
	 * it updates the start date, time, and time zone fields with the values from the running entry.
	 *
	 * @param type The type of recording (working time or project time).
	 * @param runningEntry The running entry containing the start information.
	 * @param forceUpdate If true, always overwrite the start fields from the running entry, even when the user has modified or focused them.
	 */
	private setStartIfNotChanged(type: RecordingType, runningEntry: RunningEntry, forceUpdate: boolean): void {
		if (forceUpdate || !this.changedFields.has(`${type}.startDate`) && this.notFocused(`${type}.startDate`) &&
			!this.changedFields.has(`${type}.startTime`) && this.notFocused(`${type}.startTime`) &&
			!this.changedFields.has(`${type}.startTimeZone`) && this.notFocused(`${type}.startTimeZone`)) {
			const start = parseISOZonedDateTime(runningEntry.start);
			this.config.panel.find(`[name='${type}.startDate']`).val(formatDate(start));
			this.config.panel.find(`[name='${type}.startTime']`).val(formatTime(start));
			this.config.panel.find(`[name='${type}.startTimeZone']`).val(start?.offset ?? "");
		}
	}

	/**
	 * Updates the start and end times for both working time and project time.
	 * If neither is running, it sets the start time to the current time.
	 * It also updates the end time for non-running entries and toggles the visibility
	 * of date and time zone fields based on the current state.
	 */
	private updateTime(): void {
		if (!this.isWorkingTimeRunning()) {
			this.setStartToNowIfNotChanged("workingTime");
		}
		if (!this.isProjectTimeRunning()) {
			this.setStartToNowIfNotChanged("projectTime");
		}
		this.updateEndTimeForNonRunningEntries();
		this.toggleDateFields();
		this.toggleTimeZoneFields();
	}

	/**
	 * Sets the start date, time, and time zone fields to the current date and time
	 * if they have not been changed or focused.
	 *
	 * @param type The type of recording (working time or project time).
	 */
	private setStartToNowIfNotChanged(type: RecordingType): void {
		if (!this.changedFields.has(`${type}.startDate`) && this.notFocused(`${type}.startDate`) &&
			!this.changedFields.has(`${type}.startTime`) && this.notFocused(`${type}.startTime`) &&
			!this.changedFields.has(`${type}.startTimeZone`) && this.notFocused(`${type}.startTimeZone`)) {

			const now = Temporal.Now.zonedDateTimeISO();
			this.setStart(type, now);
		}
	}

	/**
	 * Sets the start date, time, and time zone fields to the specified date.
	 *
	 * @param type The type of recording (working time or project time).
	 * @param date The date to set for the start fields.
	 */
	private setStart(type: "workingTime" | "projectTime", date: Temporal.ZonedDateTime | null): void {
		this.config.panel.find(`[name='${type}.startDate']`).val(formatDate(date));
		this.config.panel.find(`[name='${type}.startTime']`).val(formatTime(date));
		this.config.panel.find(`[name='${type}.startTimeZone']`).val(date?.offset ?? "");
	}

	/**
	 * Updates the end time fields for non-running entries to the current date and time.
	 * If the end date, time, and time zone fields have not been changed or focused,
	 * this method sets them to the current date and time.
	 * It also updates the working time and project time durations.
	 */
	private updateEndTimeForNonRunningEntries(): void {
		if (!this.changedFields.has("combinedRecording.endDate") && this.notFocused("combinedRecording.endDate") &&
			!this.changedFields.has("combinedRecording.endTime") && this.notFocused("combinedRecording.endTime") &&
			!this.changedFields.has("combinedRecording.endTimeZone") && this.notFocused("combinedRecording.endTimeZone")) {

			const now = Temporal.Now.zonedDateTimeISO();
			this.config.panel.find("[name='combinedRecording.endDate']").val(formatDate(now));
			this.config.panel.find("[name='combinedRecording.endTime']").val(formatTime(now));
			this.config.panel.find("[name='combinedRecording.endTimeZone']").val(now.offset);
		}

		this.updateWorkingTimeDuration();
		this.updateProjectTimeDuration();

		// trigger updates for duration of sidebar status badges and in timeline running records
		if (this.isAnythingRunning()) {
			this.onStatusUpdate(this.getRecordingProcedure());
		}
	}

	/**
	 * Updates the duration of the running project time.
	 * If the project time is paused, it calculates and sets the break time.
	 * It also updates the duration field in the UI.
	 */
	private updateProjectTimeDuration(): void {
		const runningProjectTime = this.getRunningProjectTime();
		if (runningProjectTime !== null) {
			let breakTime = runningProjectTime.breakTime;
			const breakTimeField = this.config.panel.find("[name='projectTime.breakTime']");
			if (runningProjectTime.status === RunningTimeStatus.PAUSED && !this.changedFields.has("projectTime.breakTime") && this.notFocused("projectTime.breakTime")) {
				breakTime = calculateBreakTime(runningProjectTime);
				breakTimeField.val(breakTime);
			}

			// keep break time visible for no-position flow (or non-zero value), but disable manual edits in paused mode and whenever break-time-with-position flow is active.
			const isBreakTimeNoPositionApplicable = this.isBreakTimeNoPositionApplicable();
			breakTimeField.closest(".form-group").toggle(isBreakTimeNoPositionApplicable || breakTime !== 0);
			if (runningProjectTime.status === RunningTimeStatus.PAUSED || !isBreakTimeNoPositionApplicable) {
				Input.disable(this.config.panel.find("[name='projectTime.breakTime']"));
			}

			this.config.panel.find("#projectTimeContainer span[data-duration]").text(formatDurationSince(runningProjectTime.start, breakTime, false));
		}
	}

	/**
	 * Updates the duration of the running working time.
	 * If the working time is paused, it calculates and sets the break time.
	 * It also updates the duration field in the UI.
	 */
	private updateWorkingTimeDuration(): void {
		const runningWorkingTime = this.getRunningWorkingTime();
		if (runningWorkingTime !== null) {
			let breakTime = runningWorkingTime.breakTime;
			const breakTimeField = this.config.panel.find("[name='workingTime.breakTime']");
			if (runningWorkingTime.status === RunningTimeStatus.PAUSED && !this.changedFields.has("workingTime.breakTime") && this.notFocused("workingTime.breakTime")) {
				breakTime = calculateBreakTime(runningWorkingTime);
				breakTimeField.val(breakTime);
			}

			// keep break time visible for no-position flow (or non-zero value), but disable manual edits in paused mode and whenever break-time-with-position flow is active.
			const isBreakTimeNoPositionApplicable = this.isBreakTimeNoPositionApplicable();
			breakTimeField.closest(".form-group").toggle(isBreakTimeNoPositionApplicable || breakTime !== 0);
			if (runningWorkingTime.status === RunningTimeStatus.PAUSED || !isBreakTimeNoPositionApplicable) {
				Input.disable(this.config.panel.find("[name='workingTime.breakTime']"));
			}

			this.config.panel.find("#workingTimeContainer span[data-duration]").text(formatDurationSince(runningWorkingTime.start, breakTime, false));
		}
	}

	/**
	 * Update visibility of buttons in bottom button bar
	 */
	private updateButtons(): void {
		if (this.isWorkingTimeRunning() || this.isProjectTimeRunning()) {
			const start = this.config.panel.find("[data-action='start']").hide();
			this.config.panel.find("#combinedRecordingStartProjectTimeButton").toggle(!this.isProjectTimeRunning());
			this.config.panel.find("[data-action='switch']").show();

			// show stop button if any record is running except break time with position currently running
			this.config.panel.find("[data-action='stop']").toggle(!this.isBreakTimeWithPositionRunning());

			// the switch button is primary if no record is paused (otherwise the resume button is shown, which already has primary style)
			const showResumeButton = this.isProjectTimePaused() || this.isWorkingTimePaused() && (this.isProjectTimePaused() || this.isOnlyWorkingTimeRunning());
			this.setButtonAsPrimary(this.config.panel.find("button[data-action='switch']"), !showResumeButton);
			this.setButtonAsPrimary(start, false);
		} else {
			const start = this.config.panel.find("[data-action='start']").show();
			this.config.panel.find("[data-action='stop'], [data-action='switch'], #combinedRecordingStartProjectTimeButton").hide();
			this.setButtonAsPrimary(this.config.panel.find("[data-action='switch']"), false);
			this.setButtonAsPrimary(start, true);
		}
		this.config.panel.find("[data-action='delete']").toggle(this.isAnythingRunning());

		this.updateBreakTimeButtons();
	}

	/**
	 * Updates the visibility and status of the pause buttons based on the current working time and project time.
	 * A dropdown menu with options to resume or pause a single record is shown to resolve the state when a single record is paused and the other is running.
	 */
	private updateBreakTimeButtons(): void {
		// show resume button if all currently running records have the status PAUSED (or the working time it is also relevant whether the project time - Both mode - is also running)
		const showResumeButton = this.isProjectTimePaused() || this.isWorkingTimePaused() && (this.isProjectTimePaused() || this.isOnlyWorkingTimeRunning());

		// show pause button if all currently running records does not have the status PAUSED (and at least one record is running)
		this.config.panel.find("#combinedRecordingPauseButton").toggle(!showResumeButton && this.isAnythingRunning());

		// show resume button if all currently running records have the status PAUSED
		this.config.panel.find("#combinedRecordingResumeButton").toggle(showResumeButton);

		// if both records are running and one of them is paused, show the dropdown menu to resolve this state (resume or pause a single record)
		if (this.isWorkingTimeRunning() && this.isProjectTimeRunning() && this.isWorkingTimePaused() !== this.isProjectTimePaused()) {
			this.config.panel.find("#combinedRecordingPauseDropdown").show();
			this.config.panel.find("[data-only-working-time-paused]").toggle(this.isWorkingTimePaused());
			this.config.panel.find("[data-only-project-time-paused]").toggle(this.isProjectTimePaused());
		} else {
			// hide the dropdown menu if both records are paused or not paused
			this.config.panel.find("#combinedRecordingPauseDropdown").hide();
		}
	}

	/**
	 * Activates logic for periodical update of the panel
	 */
	private activateUpdater(): void {
		this.updateEndTimeForNonRunningEntries();
		this.fetchRecordingStatus(true);
		this.startPeriodicalUpdate();
	}

	/**
	 * Deactivates logic for periodical update of panel
	 */
	private deactivateUpdater(): void {
		this.stopPeriodicalUpdate();
	}

	/**
	 * Starts periodical updater for time fields
	 */
	private startTimeUpdater(): void {
		setTimeout(() => {
			this.updateTime();
			this.continueTimeUpdater();
		}, 60000 - Date.now() % 60000);
	}

	/**
	 * Continue time updater
	 */
	private continueTimeUpdater(): void {
		setInterval(() => this.updateTime(), 60 * 1000);
	}

	/**
	 * Starts periodical updater mechanism
	 */
	private startPeriodicalUpdate(): void {
		this.stopPeriodicalUpdate();
		this.periodicalExecutor = setInterval(() => this.fetchRecordingStatus(true), CombinedRecordingStatusPanel.PERIODICAL_UPDATER_INTERVAL_MS);
	}

	/**
	 * Stops periodical updater mechanism
	 */
	private stopPeriodicalUpdate(): void {
		if (this.periodicalExecutor) {
			clearInterval(this.periodicalExecutor);
			this.periodicalExecutor = null;
		}
	}

	/**
	 * Toggles visibility of date fields inside combined recording
	 * These are only visible if the date of any start or end field doesn't match the current day
	 */
	private toggleDateFields(): void {
		// Find all start and end date input fields
		const showDateFields = this.config.panel.find("input[name$='startDate'], input[name$='endDate']")
			// Filter fields where the date is not the current day
			.filter((i, e) => {
				const value = $(e).val() as string;
				return !isSameDay(parseDateInput(value), Temporal.Now.plainDateISO());
			})
			// Check if any date fields are not the current day
			.length > 0;

		// Toggle the class to show or hide date fields/class
		this.config.panel.find(".status-panel-columns").toggleClass("date-field-shown", showDateFields);
		this.config.panel.find("[data-date-field]").toggle(showDateFields);
		this.config.panel.find("[data-time-field]").toggle(!showDateFields);
	}

	/**
	 * Toggles visibility of time zone fields
	 */
	private toggleTimeZoneFields(): void {
		// show time zone if client setting is active or any start/end timezone differs
		const endTimeZone = this.config.panel.find("[name='combinedRecording.endTimeZone']").val() as string;
		const hasDifferentStartTimeZone = this.config.panel.find("[name$='startTimeZone']")
			.toArray()
			.some(field => $(field).val() as string !== endTimeZone);
		const showTimeZone = globals.showTimeZone || hasDifferentStartTimeZone;
		this.config.panel.find(".status-panel-columns").toggleClass("time-zone-field-shown", showTimeZone);
		this.config.panel.find("[data-time-zone-field]").toggle(showTimeZone);
		if (!showTimeZone) {
			this.config.panel.find("input[name$='startTime'], input[name='combinedRecording.endTime']").addClass("border-radius-right");
		}
	}

	/**
	 * Sets the specified button as primary or default based on <code>primary</code>.
	 *
	 * @param button The jQuery object representing the button.
	 * @param primary A boolean indicating whether to set the button as primary.
	 */
	private setButtonAsPrimary(button: JQuery, primary: boolean): void {
		button.addClass(primary ? CombinedRecordingStatusPanel.BUTTON_CLASS_PRIMARY : CombinedRecordingStatusPanel.BUTTON_CLASS_DEFAULT);
		button.removeClass(primary ? CombinedRecordingStatusPanel.BUTTON_CLASS_DEFAULT : CombinedRecordingStatusPanel.BUTTON_CLASS_PRIMARY);
		button.toggleClass(CombinedRecordingStatusPanel.BUTTON_CLASS_XLARGE, primary);
	}

	/**
	 * Sets the loading state for the specified action button (e.g., start, stop, switch).
	 * This method also disables other elements of combined recording which must not be clickable while the action is in progress (or enables it again).
	 *
	 * @param button The jQuery object representing the button to set as loading (e.g., start, stop, switch)
	 * @param loading If true, the button will be set to loading state; otherwise, it will be reset.
	 */
	private setActionButtonLoadingState(button: JQuery, loading: boolean): void {
		if (loading) {
			Button.setLoading(button)
		} else {
			Button.resetLoading(button);
		}

		// disable end time row and start project time button while button is loading
		this.config.panel.find(".end-time-row, #combinedRecordingStartProjectTimeButton").prop("disabled", loading);

		// ensure hover dropdown of action buttons (e.g. switch recording) are closed while actions are running
		if (loading) {
			this.config.panel.find("div[id^='combinedRecording'][id$='Dropdown']").removeClass("open");
		}
	}

	private selectTask(task: Task): void {
		this.config.panel.find("input[name='projectTime.task.id']").val(task.id);
		this.setBillable(task.billable);

		this.changedFields.add("projectTime.billable");
		this.changedFields.add("projectTime.task");
		this.projectTimeInfoPanel.update(task.id, task.description, this.currentStatus?.projectTimeStatus?.runningProjectTime);
		this.updatePanelAppearanceForRecordingState();
		this.queueUpdate("projectTime");
		this.onTaskChanged();
	}

	private setBillable(billable: boolean): void {
		this.config.panel.find("#projectTimeStatusBillable").prop("checked", billable);
		this.config.panel.find(`input[name="projectTime.billable"]`).val(String(billable));
	}

	private getBillable(): boolean {
		return this.config.panel.find("#projectTimeStatusBillable").is(":checked");
	}

	/**
	 * Updates the enabled/disabled state of start date, start time, and start time zone input fields for a given recording type.
	 *
	 * @param type of recording for which the fields' state is being updated.
	 * @param enabled indicating whether the input fields should be enabled (true) or disabled (false).
	 */
	private setStartDateTimeFieldsState(type: RecordingType, enabled: boolean): void {
		this.config.panel
			.find(`
						input[name='${type}.startDate'],
						input[name='${type}.startTime'],
						input[name='${type}.startTimeZone'],
						input[name='${type}.breakTime']`)
			.prop("disabled", () => !enabled);
	}

	/**
	 * Updates enabled state of shared end date/time fields.
	 * End date/time can be changed if at least one affected recording is editable.
	 * Special (rare) case: if project time is not editable - while working time is editable - in combined mode, shared times stay locked, see TIMR-11845
	 */
	private setEndDateTimeFieldsState(enabled: boolean): void {
		this.config.panel
			.find(`
						input[name='combinedRecording.endDate'],
						input[name='combinedRecording.endTime'],
						input[name='combinedRecording.endTimeZone']`)
			.prop("disabled", () => !enabled);
		this.config.panel.find(".setEndToNow").prop("disabled", !enabled);
		this.config.panel.find(".end-time-row").toggleClass("disabled", !enabled);
	}

	/**
	 * Determines whether shared end date/time fields should be editable.
	 * End date/time can be changed if at least one affected recording is editable. In combined mode, if project time is not editable, shared times stay locked, see TIMR-11845
	 */
	private isEndDateTimeChangeAllowed(): boolean {
		if (!this.isAnythingRunning()) {
			return false;
		}

		const workingTimeChangeAllowed = isWorkingTimeDateTimeChangeAllowed();
		const projectTimeChangeAllowed = isProjectTimeDateTimeChangeAllowed();

		// rare configuration: keep shared times locked when project time is not editable.
		if (this.config.activeRecordingProcedure === RecordingProcedure.Both && !projectTimeChangeAllowed) {
			return false;
		}

		// if working time is not editable, but project time is editable, end time is editable only while project time is actually running
		if (this.config.activeRecordingProcedure === RecordingProcedure.Both &&
			!workingTimeChangeAllowed && projectTimeChangeAllowed) {
			return this.isProjectTimeRunning();
		}

		// in case only working time is active, end time is editable if workingTimeChangeAllowed
		if (this.config.activeRecordingProcedure === RecordingProcedure.WorkingTime) {
			return workingTimeChangeAllowed;
		}

		// in case only project time is active, end time is editable if projectTimeChangeAllowed
		if (this.config.activeRecordingProcedure === RecordingProcedure.ProjectTime) {
			return projectTimeChangeAllowed;
		}

		// in all other cases end time is editable if at least one of them is editable
		return workingTimeChangeAllowed || projectTimeChangeAllowed;
	}

	/**
	 * Validates action execution for edited end time.
	 * If end time is dirty and the action affects non-editable recording type(s), the action is blocked, see TIMR-11845.
	 */
	private isActionAllowedForEditedEndTime(procedure: RecordingProcedure): boolean {
		// if end time is not changed/dirty, allow all actions
		if (!this.changedEnd) {
			return true;
		}

		// error dialog to show when action is not allowed for edited end time
		const errorDialog = () => BootstrapModal.error({
			title: "combinedRecording.endTimeEdited.actionNotPossible.title",
			message: "combinedRecording.endTimeEdited.actionNotPossible.message"
		});

		// if end time is changed/dirty, only allow actions that affect working time when working time is editable (show error dialog and block action otherwise)
		if (isWorkingTimeIncluded(procedure) && !isWorkingTimeDateTimeChangeAllowed()) {
			errorDialog();
			return false;
		}

		// if end time is changed/dirty, only allow actions that affect project time when project time is editable (show error dialog and block action otherwise)
		if (isProjectTimeIncluded(procedure) && !isProjectTimeDateTimeChangeAllowed()) {
			errorDialog();
			return false;
		}

		// allow action in all other cases (e.g. end time is changed but action does not affect any of the recording types, or end time is changed and both recording types are editable)
		return true;
	}

	private onTaskChanged(): void {
		this.projectTimeDescriptionTypeahead.setFilter({taskId: this.taskDropdown.getSelectedId()});

		// update visibility and toggle status of the button after task change (do not change the description itself)
		this.updateDescriptionField("projectTime", this.taskDropdown.getSelectedData()?.descriptionRequired)
	}

	private selectWorkingTimeType(workingTimeType: WorkingTimeType): void {
		this.onWorkingTimeTypeChanged();
		this.changedFields.add("workingTime.workingTimeType.id");
		this.workingTimeInfoPanel.update(workingTimeType);
		this.updatePanelAppearanceForRecordingState();
		this.queueUpdate("workingTime");
	}

	private updateCustomFieldsVisibility(): void {
		this.config.panel.find("#workingTimeStatusCustomFields").toggle(this.isAttendanceTimeSelected());
	}

	private onWorkingTimeTypeChanged(): void {
		this.updateCustomFieldsVisibility();
		const workingTimeType = this.getSelectedWorkingTimeType();
		this.workingTimeDescriptionTypeahead.setFilter(workingTimeType ? {workingTimeTypeId: workingTimeType.id} : {});
		this.updateFlowState();
	}

	private getSelectedWorkingTimeType(): WorkingTimeType {
		const workingTimeTypeId = this.parseInt(this.config.panel.find("[name='workingTime.workingTimeType.id']"));
		const allWorkingTimeTypes = this.config.workingTimeTypesForRecording.concat(this.config.breakTimeTypes);
		const workingTimeTypes = $.grep(allWorkingTimeTypes, wtt => wtt.id === workingTimeTypeId);
		return workingTimeTypes.length > 0 ? workingTimeTypes[0] : null;
	}

	/**
	 * Deletes the respective fields of the given procedure from the array with the fields that were changed
	 * All unaffected fields remain, furthermore, the description typeahead is reset
	 * This is necessary for certain actions like stop, switch or delete
	 * @param procedure working time, project time or both
	 */
	private clearChangedFields(procedure: RecordingProcedure): void {
		/* those fields that concern the respective procedure are deleted/filtered out:
		 * - always filter out combined fields (currently only end time)
		 * - working time fields if procedure includes working time
		 * - project time fields if procedure includes project time
		 */
		this.changedFields = this.filterSet((field: string) =>
			!field.startsWith("combinedRecording") &&
			!(isWorkingTimeIncluded(procedure) && field.startsWith("workingTime")) &&
			!(isProjectTimeIncluded(procedure) && field.startsWith("projectTime"))
		);

		this.descriptionTypeaheadNeedReset(procedure);
	}

	private descriptionTypeaheadNeedReset(procedure: RecordingProcedure) {
		// reset typeahead
		if (this.isWorkingTimeEnabled() && isWorkingTimeIncluded(procedure)) {
			this.workingTimeDescriptionTypeahead.setNeedsReset();
		}
		if (this.isProjectTimeEnabled() && isProjectTimeIncluded(procedure)) {
			this.projectTimeDescriptionTypeahead.setNeedsReset();
		}
	}

	/**
	 * Sets the start date and time as changed for the specified recording type.
	 *
	 * @param type The type of recording (working time or project time).
	 */
	private setStartDateTimeChanged(type: RecordingType): void {
		switch (type) {
			case "workingTime":
				this.changedWorkingTimeStart = true;
				break;
			case "projectTime":
				this.changedProjectTimeStart = true;
				break;
		}
	}

	/**
	 * Checks if the date time of the specified recording has been changed
	 * This also considers the given procedure e.g. 	both records are running, user modifies end time and switches (only) project time
	 * 											In this case the running working time is not changed and should not get the changed state
	 *
	 * @param type The type of recording (working time or project time).
	 * @param procedure The recording procedure to check.
	 * @returns `true` if the specified recording type has been changed for the given procedure, otherwise `false`.
	 */
	private isDateTimeChanged(type: RecordingType, procedure: RecordingProcedure): boolean {
		switch (type) {
			case "workingTime":
				return isWorkingTimeIncluded(procedure) && (this.changedWorkingTimeStart || this.changedEnd);
			case "projectTime":
				return isProjectTimeIncluded(procedure) && (this.changedProjectTimeStart || this.changedEnd);
		}
	}

	/**
	 * Triggers method to update timeline outside (below) combined recording.
	 * DEVNOTE: The method that is triggered outside this class checks whether the change affects/changes the current selected time frame
	 * 			Only in this case is it reloaded.
	 */
	private updateTimeline(forceReload?: boolean): void {
		this.config.onUpdateTimeline(this.currentStatus, forceReload);
	}

	private isSameDateTime(firstDateTime: string, secondDateTime: string): boolean {
		if (!firstDateTime || !secondDateTime) {
			return false;
		}
		const first = parseISOZonedDateTime(firstDateTime);
		const second = parseISOZonedDateTime(secondDateTime);
		// Same instant means same time, regardless of offset; equals() would also require the same zone.
		return first !== null && second !== null && Temporal.ZonedDateTime.compare(first, second) === 0;
	}

	/**
	 * Determines whether the break time recording mode is set to record with a position and if the given procedure satisfies required conditions.
	 *
	 * @param {RecordingProcedure} procedure - The recording procedure to be checked for working time inclusion.
	 * @return {boolean} Returns true if break time recording mode is set to record with position, working time is enabled, and the procedure includes working time; otherwise, returns false.
	 */
	private isBreakTimeWithPositionApplicable(procedure: RecordingProcedure): boolean {
		return globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_WITH_POSITION
			&& this.isWorkingTimeEnabled()
			&& isWorkingTimeIncluded(procedure);
	}

	/**
	 * Determines whether to use the "no position" flow for break time based on the configured recording mode
	 * There are two cases in which pause times without position specification should apply:
	 * - if break mode is set to RECORD_NO_POSITION
	 * - if break mode is configured to RECORD_WITH_POSITION; project time only is active in combined recording -> fallback to "no position"
	 * @return {boolean} True if the "no position" flow should be used, otherwise false.
	 */
	private isBreakTimeNoPositionApplicable(): boolean {
		return globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_NO_POSITION ||
			globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_WITH_POSITION && !this.isWorkingTimeEnabled() && this.isProjectTimeEnabled();
	}

	/**
	 * Checks if the working time is paused.
	 *
	 * @returns {boolean} True if a (running) working time is available and is paused or categorized as break time, false otherwise.
	 */
	private readonly isWorkingTimePaused = (): boolean => this.isWorkingTimeEnabled() && this.getRunningWorkingTime()?.workingTimeType.category === WorkingTimeTypeCategory.BreakTime || this.getRunningWorkingTime()?.status === RunningTimeStatus.PAUSED;


	/**
	 * Checks if the project time is paused.
	 *
	 * @returns {boolean} True if a (running) project time is available and is paused, false otherwise.
	 */
	private readonly isProjectTimePaused = (): boolean => this.isProjectTimeEnabled() && this.getRunningProjectTime()?.status === RunningTimeStatus.PAUSED;

	/**
	 * Filters changedFields set based on the provided predicate.
	 * DEVNOTE: Set is not directly filterable, so it is converted to an array, filtered and converted back, see https://stackoverflow.com/a/75833740
	 *
	 * @param predicate Function that takes a field name as input and returns a boolean indicating whether the field should be included in the filtered set.
	 * @returns New set containing only the fields that satisfy the predicate.
	 */
	private filterSet(predicate: (field: string) => boolean): Set<string> {
		return new Set([...this.changedFields].filter(predicate));
	}

	/**
	 * Checks if the specified field is not currently focused.
	 *
	 * @param fieldId The ID of the field to check.
	 * @returns True if the field is not focused, false otherwise.
	 */
	private readonly notFocused = (fieldId: string): boolean => this.currentFocusedField !== fieldId;

	/**
	 * Parses the value of the specified jQuery field as an integer.
	 *
	 * @param field The jQuery field to parse.
	 * @returns The integer value of the field.
	 */
	private readonly parseInt = (field: JQuery): number => parseInt(field.val() as string, 10);

	/**
	 * Checks if only the project time is running.
	 * DEVNOTE: This only verifies if a running project time object is available in the response of the backend
	 * 			not verifies if the status is RUNNING (can be PAUSED)
	 *
	 * @returns True if only the project time is running, false otherwise.
	 */
	private readonly isOnlyProjectTimeRunning = (): boolean => !this.isWorkingTimeRunning() && this.isProjectTimeRunning();

	/**
	 * Checks if only the working time is running.
	 * DEVNOTE: This only verifies if a running working time object is available in the response of the backend
	 * 			not verifies if the status is RUNNING (can be PAUSED)
	 *
	 * @returns True if only the working time is running, false otherwise.
	 */
	private readonly isOnlyWorkingTimeRunning = (): boolean => this.isWorkingTimeRunning() && !this.isProjectTimeRunning();

	/**
	 * Checks if either the working time or project time is running.
	 *
	 * @returns True if either the working time or project time is running, false otherwise.
	 */
	private readonly isAnythingRunning = (): boolean => this.isWorkingTimeRunning() || this.isProjectTimeRunning();

	/**
	 * Checks if a running working time is available.
	 * DEVNOTE: This only verifies if a running working time object is available in the response of the backend
	 * 			not verifies if the status is RUNNING (can be PAUSED)
	 *
	 * @returns True if the working time is running, false otherwise.
	 */
	private readonly isWorkingTimeRunning = (): boolean => this.isWorkingTimeEnabled() && this.getRunningWorkingTime() !== null;

	/**
	 * Checks if a running project time is available.
	 * DEVNOTE: This only verifies if a running project time object is available in the response of the backend
	 * 			not verifies if the status is RUNNING (can be PAUSED)
	 *
	 * @returns True if the project time is running, false otherwise.
	 */
	private readonly isProjectTimeRunning = (): boolean => this.isProjectTimeEnabled() && this.getRunningProjectTime() !== null;

	private readonly isWorkingTimeEnabled = (): boolean => isWorkingTimeIncluded(this.config.activeRecordingProcedure);

	private readonly isProjectTimeEnabled = (): boolean => isProjectTimeIncluded(this.config.activeRecordingProcedure);

	/**
	 * Checks if the break time recording mode is set to record with position and the currently running working time is categorized as break time.
	 *
	 * @returns True if the break time recording mode is set to record with position and the currently running working time is categorized as break time, false otherwise.
	 */
	private readonly isBreakTimeWithPositionRunning = (): boolean => globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_WITH_POSITION
		&& this.getRunningWorkingTime()?.workingTimeType?.category === WorkingTimeTypeCategory.BreakTime;
}
